import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  Droplets, Dog, DoorOpen, TrendingUp, RefreshCw,
  AlertTriangle, CheckCircle, Clock, ChevronLeft,
  ChevronRight, Info, Printer, LogIn, LogOut,
  BedDouble, ShowerHead, Flame, CalendarDays, BarChart3,
  TrendingDown, Search, ArrowUpDown, ArrowUp, ArrowDown, MessageSquare, ExternalLink,
  Activity, Repeat, Tag, Package, Home, Utensils, Pill, Check, Pencil,
  Loader2, PlusCircle
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import OccupancyTrend from "../components/OccupancyTrend";
import PackageRenewalsTab from "../components/PackageRenewalsTab";
import RequestedTab from "../components/RequestedTab";
import { Inbox as InboxIcon, LayoutGrid } from "lucide-react";
import { OpsSectionHead } from "@/components/OpsSectionHead";
import "@/styles/ops-v3.css";

// Resort-local calendar date (YYYY-MM-DD, Eastern) — avoids the UTC rollover after ~8 PM ET.
const localToday = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
import { InBuildingDialog } from "@/components/InBuildingDialog";
import { StandingBadge, StandingBreakdownLine } from "../components/StandingBadge";
import { Link, useSearchParams } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, Legend, ResponsiveContainer, ReferenceLine,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Booking Curve chart: service definitions + shared UI ─────────────
const SERVICE_DEFS = [
  { key: 'boarding', label: 'Boarding', color: '#1F8A7C' },
  { key: 'daycare', label: 'Daycare', color: '#8B8AE0' },
  { key: 'bath', label: 'Bath', color: '#D4A876' },
];

function DateRangePickers({ startDate, endDate, onStart, onEnd, onReset }) {
  const parse = (s) => (s ? new Date(s + 'T12:00:00') : undefined);
  const label = (s) => (s ? new Date(s + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Pick date');
  const toISO = (d) => { const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000); return z.toISOString().split('T')[0]; };
  const DateBtn = ({ value, onChange, testid }) => {
    const [open, setOpen] = useState(false);
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 px-3 text-xs font-medium gap-2" data-testid={testid}>
            <CalendarDays className="h-3.5 w-3.5 text-gray-400" />{label(value)}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" selected={parse(value)} onSelect={(d) => { if (d) { onChange(toISO(d)); setOpen(false); } }} initialFocus />
        </PopoverContent>
      </Popover>
    );
  };
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Date range</span>
      <div className="flex flex-wrap items-center gap-1.5">
        <DateBtn value={startDate} onChange={onStart} testid="chart-start-date" />
        <span className="text-gray-400 text-xs">&rarr;</span>
        <DateBtn value={endDate} onChange={onEnd} testid="chart-end-date" />
        <Button variant="ghost" size="sm" className="h-8 px-2 text-xs text-emerald-600 font-semibold" onClick={onReset} data-testid="chart-reset-range">&plusmn;2 wks</Button>
      </div>
    </div>
  );
}

function BookingTooltip({ active, payload, services, showYoY }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0]?.payload || {};
  const total = services.reduce((a, k) => a + (row[k] || 0), 0);
  const d = row.date ? new Date(row.date + 'T12:00:00') : null;
  return (
    <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg min-w-[150px]">
      <div className="font-semibold mb-1">{row.weekday}{d ? `, ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}{row.isWeekend ? ' · Wknd' : ''}</div>
      {SERVICE_DEFS.filter((s) => services.includes(s.key)).map((s) => (
        <div key={s.key} className="flex items-center justify-between gap-4">
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />{s.label}</span>
          <span className="font-bold tabular-nums">{row[s.key] || 0}</span>
        </div>
      ))}
      {services.length > 1 && (
        <div className="flex items-center justify-between gap-4 border-t border-white/20 mt-1 pt-1"><span>Total</span><span className="font-bold tabular-nums">{total}</span></div>
      )}
      {showYoY && row.yoyTotal != null && <div className="text-gray-300 mt-1">Last year: {row.yoyTotal}</div>}
    </div>
  );
}

// ─── Inline editable room cell ────────────────────────────────────────
function EditableRoom({ value, animalId, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value || ''); }, [value]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const save = async () => {
    const trimmed = draft.trim();
    if (trimmed === (value || '').trim()) { setEditing(false); return; }
    try {
      await axios.put(`${API}/ops/room-assignment`, { animal_id: animalId, room: trimmed });
      onSaved?.(animalId, trimmed);
      toast.success(`Room updated to "${trimmed}"`);
    } catch { toast.error('Failed to save room'); }
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        data-testid={`room-edit-input-${animalId}`}
        className="w-28 px-1.5 py-0.5 text-xs border border-blue-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); } }}
        placeholder="Room #"
      />
    );
  }

  const isExact = value && /\d/.test(value);
  return (
    <span
      data-testid={`room-cell-${animalId}`}
      className={`cursor-pointer px-1.5 py-0.5 rounded text-xs inline-block min-w-[60px] border border-dashed transition-colors ${
        isExact ? 'text-emerald-700 bg-emerald-50 border-emerald-200 hover:bg-emerald-100' :
        value ? 'text-amber-700 bg-amber-50 border-amber-200 hover:bg-amber-100' :
        'text-gray-400 bg-gray-50 border-gray-200 hover:bg-gray-100'
      }`}
      title="Click to edit room number"
      onClick={() => setEditing(true)}
    >
      {value || '— assign —'}
    </span>
  );
}

// ─── Print helpers ────────────────────────────────────────────────────
// Convert 12-hour time to 24-hour for proper sorting (09:00 AM → 09:00, 04:03 PM → 16:03)
function timeTo24(t) {
  if (!t) return '99:99';
  const m = t.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!m) return '99:99';
  let h = parseInt(m[1]);
  const min = m[2];
  const ampm = m[3].toUpperCase();
  if (ampm === 'PM' && h !== 12) h += 12;
  if (ampm === 'AM' && h === 12) h = 0;
  return `${String(h).padStart(2,'0')}:${min}`;
}

// Build staff-friendly print HTML for bath list — large fonts, fills the page
function printBathList(baths, dateLabel, date) {
  const win = window.open('', '_blank');
  const sorted = [...baths].sort((a, b) => timeTo24(a.checkout_time).localeCompare(timeTo24(b.checkout_time)));
  const rows = sorted.map((b, i) => {
    const notes = [
      ...(b.flags || []),
      b.medicines ? `Meds: ${b.medicines}` : '',
      b.allergies && b.allergies.toLowerCase() !== 'none' ? `Allergy: ${b.allergies}` : '',
      b.reservation_notes || '',
    ].filter(Boolean).join('; ');
    const grp = GROUP_META[b.group];
    const grpCell = grp ? `<span class="grp grp-${b.group}">${grp.short}</span>` : '—';
    return `<tr><td class="rn">${i + 1}</td><td class="dog">${b.animal_name}</td><td class="room">${b.room || '—'}</td><td class="grpcell">${grpCell}</td><td class="${(b.bath_type || '').toLowerCase() === 'hypoallergenic' ? 'hypo' : ''}">${(b.bath_type || 'Standard')}${b.is_fresh_n_clean ? ' + Fresh n Clean' : ''}</td><td>${b.checkout_time}</td><td>${b.is_completed ? 'Done' : '—'}</td><td class="notes">${notes || '—'}</td></tr>`;
  }).join('');
  const gc = { large: 0, small: 0, pp: 0 };
  baths.forEach(b => { if (b.group && gc[b.group] !== undefined) gc[b.group]++; });
  win.document.write(`<html><head><title>Bath List</title><style>
    body{font-family:Arial,sans-serif;padding:16px;font-size:18px;line-height:1.4}
    h1{font-size:28px;margin:0 0 4px;font-weight:800}
    h2{font-size:18px;color:#666;margin:0 0 12px;font-weight:normal}
    table{width:100%;border-collapse:collapse}
    th{text-align:left;padding:8px 10px;border-bottom:3px solid #333;font-size:16px;text-transform:uppercase;color:#555}
    td{padding:8px 10px;border-bottom:2px solid #e0e0e0;font-size:20px}
    td.dog{font-weight:700;font-size:22px}
    td.room{font-weight:600;font-size:20px}
    td.notes{font-size:16px;color:#555;white-space:normal;max-width:300px}
    td.rn{color:#aaa;font-size:16px;width:30px}
    td.grpcell{width:52px}
    .grp{display:inline-block;font-weight:800;font-size:15px;padding:2px 8px;border-radius:10px;border:1.5px solid}
    .grp-large{background:#d1fae5;color:#065f46;border-color:#6ee7b7}
    .grp-small{background:#e0f2fe;color:#075985;border-color:#7dd3fc}
    .grp-pp{background:#ffe4e6;color:#9f1239;border-color:#fda4af}
    .hypo{font-weight:bold;color:#92400e}
    @media print{@page{margin:0.4in}}
  </style></head><body>
  <h1>Bath List — ${dateLabel}</h1><h2>${baths.length} bath(s) | ${date} &nbsp;·&nbsp; Lg ${gc.large} · Sm ${gc.small} · PP ${gc.pp}</h2>
  <table><thead><tr><th>#</th><th>Dog</th><th>Room</th><th>Group</th><th>Bath</th><th>Checkout</th><th>Status</th><th>Notes</th></tr></thead><tbody>${rows}</tbody></table>
  </body></html>`);
  win.document.close();
  win.print();
}

// Landscape print for the Going Home checkout / belongings checklist
function printGoingHomeList(items, dateLabel, date) {
  const win = window.open('', '_blank');
  const ICON = (paths) => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">${paths}</svg>`;
  const SVG_FOOD = ICON('<path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/><path d="M7 2v20"/><path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7"/>');
  const SVG_MEDS = ICON('<path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z"/><path d="m8.5 8.5 7 7"/>');
  const SVG_OTHER = ICON('<path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/>');
  const chip = (cls, svg, label) => `<span class="chip ${cls}">${svg}${label}</span>`;
  const sorted = [...items].sort((a, b) => (a.checkout_ts || 0) - (b.checkout_ts || 0));
  const rows = sorted.map((it, i) => {
    const b = it.belongings || {};
    const chips = [];
    if (b.food) chips.push(chip('c-food', SVG_FOOD, 'Food'));
    if (b.meds) chips.push(chip('c-meds', SVG_MEDS, 'MEDS'));
    if ((b.other || '').trim()) chips.push(chip('c-other', SVG_OTHER, b.other.trim()));
    const belong = chips.length ? chips.join('') : '<span class="check">⚠ Please double-check (collar / leash?)</span>';
    const box = it.has_belongings ? '<span class="box"></span>' : '<span class="none">—</span>';
    return `<tr><td class="rn">${i + 1}</td><td class="dog">${it.animal_name || '—'}</td><td>${it.owner_name || '—'}</td><td class="room">${it.room || '—'}</td><td>${it.checkout_time || '—'}</td><td class="belong">${belong}</td><td class="ret">${box}</td></tr>`;
  }).join('');
  const withB = items.filter(i => i.has_belongings).length;
  win.document.write(`<html><head><title>Going Home</title><style>
    body{font-family:Arial,sans-serif;padding:16px;font-size:18px;line-height:1.4}
    h1{font-size:28px;margin:0 0 4px;font-weight:800}
    h2{font-size:18px;color:#666;margin:0 0 12px;font-weight:normal}
    table{width:100%;border-collapse:collapse}
    th{text-align:left;padding:8px 10px;border-bottom:3px solid #333;font-size:15px;text-transform:uppercase;color:#555}
    td{padding:10px;border-bottom:2px solid #e0e0e0;font-size:20px;vertical-align:middle}
    td.dog{font-weight:700;font-size:22px}
    td.room{font-weight:600}
    td.belong{font-size:16px}
    td.rn{color:#aaa;font-size:16px;width:30px}
    td.ret{text-align:center;width:90px}
    .box{display:inline-block;width:26px;height:26px;border:2.5px solid #333;border-radius:4px}
    .none{color:#bbb}
    .check{color:#b45309;font-weight:700}
    .chip{display:inline-flex;align-items:center;gap:5px;padding:4px 9px;border-radius:7px;border:2px solid;font-weight:700;font-size:15px;margin:2px 5px 2px 0;line-height:1}
    .chip svg{width:16px;height:16px;flex:0 0 auto}
    .c-food{background:#ecfdf5;color:#047857;border-color:#6ee7b7}
    .c-meds{background:#fef2f2;color:#be123c;border-color:#fca5a5;font-size:16px}
    .c-other{background:#eef2ff;color:#4338ca;border-color:#a5b4fc}
    @media print{@page{margin:0.4in;size:landscape} .chip{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
  </style></head><body>
  <h1>Going Home — ${dateLabel}</h1><h2>${items.length} checking out | ${withB} with belongings to return &nbsp;·&nbsp; ${date}</h2>
  <table><thead><tr><th>#</th><th>Dog</th><th>Owner</th><th>Room</th><th>Checkout</th><th>Belongings to return</th><th>Returned</th></tr></thead><tbody>${rows}</tbody></table>
  </body></html>`);
  win.document.close();
  win.print();
}

// Popover editor: staff toggle Food/Meds + type Other belongings for one checkout.
function GoingHomeBelongEditor({ item, onSave }) {
  const [open, setOpen] = useState(false);
  const b = item.belongings || {};
  const [food, setFood] = useState(!!b.food);
  const [meds, setMeds] = useState(!!b.meds);
  const [other, setOther] = useState(b.other || '');

  useEffect(() => {
    if (open) { setFood(!!b.food); setMeds(!!b.meds); setOther(b.other || ''); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = () => { onSave(item, { food, meds, other }); setOpen(false); };
  const Toggle = ({ on, setOn, Icon, label, color, testid }) => (
    <button type="button" onClick={() => setOn(!on)} data-testid={testid}
      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold border transition-colors ${on ? color : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'}`}>
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600" title="Edit belongings" data-testid={`gh-edit-${item.reservation_id}`}>
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="ops-v3 w-64 p-3" align="end">
        <div className="text-xs font-semibold text-gray-500 mb-2">Belongings from home</div>
        <div className="flex gap-2 mb-2">
          <Toggle on={food} setOn={setFood} Icon={Utensils} label="Food" color="bg-emerald-100 text-emerald-800 border-emerald-300" testid={`gh-toggle-food-${item.reservation_id}`} />
          <Toggle on={meds} setOn={setMeds} Icon={Pill} label="Meds" color="bg-rose-100 text-rose-800 border-rose-300" testid={`gh-toggle-meds-${item.reservation_id}`} />
        </div>
        <Input value={other} onChange={e => setOther(e.target.value)} placeholder="Other (bed, toys, leash…)" className="h-9 text-sm mb-2" data-testid={`gh-other-${item.reservation_id}`} />
        <Button size="sm" className="w-full" onClick={save} data-testid={`gh-save-${item.reservation_id}`}>Save</Button>
      </PopoverContent>
    </Popover>
  );
}

// ─── Going Home Tab ───────────────────────────────────────────────────
function GoingHomeTab() {
  const [data, setData] = useState({ items: [], total: 0, with_belongings: 0, boarding_count: 0, returned_count: 0 });
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(localToday());
  const [loadedDate, setLoadedDate] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [sortDir, setSortDir] = useState('asc');

  const syncBelongings = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/ops/belongings/refresh?date=${date}`);
      if (res.data?.ok) {
        toast.success(`Belongings synced (${res.data.reservations_with_belongings || 0} dogs)`);
        fetchData(true);
      } else {
        toast.error(`Sync failed: ${res.data?.error || 'unknown'}`);
      }
    } catch (e) {
      toast.error("Belongings sync failed — try again");
    } finally {
      setSyncing(false);
    }
  };

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    const fetchingDate = date;
    try {
      const res = await axios.get(`${API}/ops/going-home-list?date=${date}`);
      setData(res.data || { items: [] });
      setLoadedDate(fetchingDate);
    } catch (e) {
      if (silent) return;
      const s = e.response?.status;
      toast.error(s === 401 || s === 403 ? "Session expired — please log in again" : "Failed to load going-home list");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [date]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    const id = setInterval(() => fetchData(true), 90000);
    return () => clearInterval(id);
  }, [fetchData]);

  const changeDate = (offset) => {
    const d = new Date(date + 'T12:00:00');
    d.setDate(d.getDate() + offset);
    setDate(d.toISOString().split('T')[0]);
  };

  const saveBelongings = async (item, patch) => {
    setData(prev => {
      const newItems = prev.items.map(it => it.reservation_id === item.reservation_id
        ? {
            ...it,
            belongings: {
              food: 'food' in patch ? patch.food : it.belongings.food,
              meds: 'meds' in patch ? patch.meds : it.belongings.meds,
              other: 'other' in patch ? patch.other : it.belongings.other,
            },
            returned: 'returned' in patch ? patch.returned : it.returned,
            has_belongings: !!(('food' in patch ? patch.food : it.belongings.food) ||
                               ('meds' in patch ? patch.meds : it.belongings.meds) ||
                               (('other' in patch ? patch.other : it.belongings.other) || '').trim()),
          }
        : it);
      return {
        ...prev,
        items: newItems,
        with_belongings: newItems.filter(i => i.has_belongings).length,
        returned_count: newItems.filter(i => i.returned).length,
      };
    });
    try {
      await axios.post(`${API}/ops/going-home-belongings`, { reservation_id: item.reservation_id, date, ...patch });
      fetchData(true); // resync with server (cache busted on write)
    } catch (e) {
      toast.error("Couldn't save — try again");
      fetchData(true);
    }
  };

  const isToday = date === localToday();
  const displayDate = new Date(date + 'T12:00:00');
  const dateLabel = isToday ? "Today" : displayDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  const printReady = !loading && loadedDate === date;
  const items = data.items || [];
  const departedCount = items.filter(i => i.has_left).length;
  const sortedItems = [...items].sort((a, b) => sortDir === 'asc' ? (a.checkout_ts || 0) - (b.checkout_ts || 0) : (b.checkout_ts || 0) - (a.checkout_ts || 0));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => changeDate(-1)} data-testid="gh-prev-day"><ChevronLeft className="h-4 w-4" /></Button>
          <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-[160px]" data-testid="gh-date-picker" />
          <Button variant="outline" size="icon" onClick={() => changeDate(1)} data-testid="gh-next-day"><ChevronRight className="h-4 w-4" /></Button>
          {!isToday && (
            <Button variant="ghost" size="sm" onClick={() => setDate(localToday())} className="text-xs">Today</Button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Badge variant="secondary" className="text-sm font-medium px-3 py-1" data-testid="gh-count">{data.total || 0} going home</Badge>
          <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-xs font-bold px-2" data-testid="gh-belongings-count">{data.with_belongings || 0} with belongings</Badge>
          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-xs font-bold px-2" data-testid="gh-returned-count">{data.returned_count || 0} returned</Badge>
          {departedCount > 0 && <Badge className="bg-gray-200 text-gray-600 border-gray-300 text-xs font-bold px-2" data-testid="gh-departed-count">{departedCount} departed</Badge>}
          <Button variant="outline" size="sm" onClick={syncBelongings} disabled={syncing} title="Pull collar/leash/bed/toys from Gingr's Belongings report" data-testid="gh-sync-belongings-btn">
            <Package className={`h-4 w-4 mr-1 ${syncing ? 'animate-pulse' : ''}`} /> {syncing ? 'Syncing…' : 'Sync belongings'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => { if (printReady) printGoingHomeList(items, dateLabel, date); }} disabled={!printReady} title={printReady ? 'Print going-home list' : 'Loading this date — please wait before printing'} data-testid="print-going-home-btn">
            <Printer className="h-4 w-4 mr-1" /> {printReady ? 'Print' : 'Loading…'}
          </Button>
          <Button variant="outline" size="icon" onClick={() => fetchData()} disabled={loading} title="Refresh" aria-label="Refresh" className="h-9 w-9">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <div className="text-sm text-gray-500">{dateLabel}'s checkouts — sorted by checkout time. Confirm every belonging is returned before the dog leaves.</div>

      {loading ? (
        <div className="py-16 text-center text-gray-400">Loading going-home list…</div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center">
          <Home className="h-10 w-10 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500 font-medium">No checkouts on this date</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm" data-testid="going-home-table">
            <thead>
              <tr className="text-left text-gray-500 border-b bg-gray-50/60">
                <th className="py-3 pl-4 pr-2 font-semibold w-8">#</th>
                <th className="py-3 px-2 font-semibold">Dog</th>
                <th className="py-3 px-2 font-semibold">Owner</th>
                <th className="py-3 px-2 font-semibold">Room</th>
                <th className="py-3 px-2 font-semibold cursor-pointer select-none hover:text-gray-800" onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')} data-testid="gh-sort-checkout" title="Sort by checkout time">
                  <span className="inline-flex items-center gap-1">Checkout {sortDir === 'asc' ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />}</span>
                </th>
                <th className="py-3 px-2 font-semibold">Belongings to return</th>
                <th className="py-3 px-2 font-semibold text-center w-28">Returned</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sortedItems.map((it, idx) => (
                <tr key={it.reservation_id} className={`hover:bg-gray-50 transition-colors ${it.has_left ? 'opacity-60' : ''} ${it.returned ? 'bg-emerald-50/40' : ''}`} data-testid={`gh-row-${it.animal_id}`}>
                  <td className="py-3 pl-4 pr-2 text-gray-400 font-medium">{idx + 1}</td>
                  <td className="py-3 px-2">
                    <div className="flex items-center gap-2.5">
                      <div className="h-9 w-9 rounded-full bg-blue-50 grid place-items-center text-blue-400 overflow-hidden shrink-0">
                        {it.image_url ? (
                          <img src={it.image_url} alt={it.animal_name} className="h-full w-full object-cover" onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                        ) : null}
                        <Dog className="h-4 w-4" style={it.image_url ? { display: 'none' } : {}} />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-800 flex items-center gap-1.5">{it.animal_name || '—'}<StandingBadge isStanding={it.is_standing} size="xs" />{it.has_left && <Badge className="bg-gray-200 text-gray-600 border-gray-300 text-[10px] px-1.5 py-0 font-semibold" data-testid={`gh-departed-${it.animal_id}`}>Departed</Badge>}</div>
                        <div className="text-xs text-gray-500">{it.breed}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-2 text-gray-700">{it.owner_name || '—'}</td>
                  <td className="py-3 px-2"><span className="text-xs font-bold bg-gray-100 text-gray-700 border border-gray-200 px-2 py-0.5 rounded">{it.room || '—'}</span></td>
                  <td className="py-3 px-2 text-gray-700"><span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-gray-400" />{it.checkout_time}</span></td>
                  <td className="py-3 px-2">
                    <div className="flex flex-wrap items-center gap-1.5">
                      {it.belongings.food && <Badge className="bg-white text-gray-700 border-gray-300 hover:bg-gray-50 text-xs font-semibold gap-1" data-testid={`gh-food-${it.animal_id}`}><Utensils className="h-3 w-3" /> Food</Badge>}
                      {it.belongings.meds && <Badge className="bg-rose-50 text-rose-700 border-rose-200 text-xs font-semibold gap-1" data-testid={`gh-meds-${it.animal_id}`}><Pill className="h-3 w-3" /> Meds</Badge>}
                      {(it.belongings.other || '').trim() && <Badge className="bg-white text-gray-700 border-gray-300 hover:bg-gray-50 text-xs font-semibold gap-1" data-testid={`gh-other-chip-${it.animal_id}`}><Package className="h-3 w-3" /> {it.belongings.other}</Badge>}
                      {!it.has_belongings && <span className="inline-flex items-center gap-1 text-xs text-amber-700 font-medium" data-testid={`gh-doublecheck-${it.animal_id}`}><AlertTriangle className="h-3.5 w-3.5" /> Please double-check (most have collar &amp; leash)</span>}
                      <GoingHomeBelongEditor item={it} onSave={saveBelongings} />
                    </div>
                  </td>
                  <td className="py-3 px-2 text-center">
                    {it.has_belongings ? (
                      <button onClick={() => saveBelongings(it, { returned: !it.returned })} data-testid={`gh-returned-${it.reservation_id}`}
                        className={`inline-flex items-center justify-center h-7 w-7 rounded border-2 transition-colors ${it.returned ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-gray-300 hover:border-emerald-400'}`}
                        title={it.returned ? 'Marked returned — click to undo' : 'Mark everything returned'}>
                        {it.returned && <Check className="h-4 w-4" />}
                      </button>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600">
        <span className="font-semibold text-gray-500">Belonging types:</span>
        <span className="inline-flex items-center gap-1"><Utensils className="h-3.5 w-3.5 text-emerald-600" /> Food from home</span>
        <span className="inline-flex items-center gap-1"><Pill className="h-3.5 w-3.5 text-rose-600" /> Medications</span>
        <span className="inline-flex items-center gap-1"><Package className="h-3.5 w-3.5 text-indigo-600" /> Other (bed, toys, leash, bowls…)</span>
        <span className="text-gray-400">— Food &amp; Meds auto-detect from Gingr; use the pencil to add anything else.</span>
      </div>
    </div>
  );
}

// Playgroup classification metadata (mirrors Today's View KPI: Lg / Sm / PP).
const GROUP_META = {
  large: { short: 'Lg', label: 'Large', cls: 'keep-color grp-lg' },
  small: { short: 'Sm', label: 'Small', cls: 'keep-color grp-sm' },
  pp: { short: 'PP', label: 'Private Play', cls: 'keep-color grp-pp' },
};

// Bucket a PP dog into a room-section column.
// Placement is driven by the RESERVATION TYPE, not the play flag:
//  • Boarding reservation  → placed in its room-number column (100s..500s / Lux).
//    A boarding dog (e.g. Leo in 407) belongs under the 400s let-outs even if
//    he also carries a Private Play / modified flag.
//  • Daycare or Day Boarding reservation → the "Day Board / Modified" column
//    (e.g. Jessa, whose reservation is day boarding). This is the ONLY thing
//    that section should contain.
const PP_COLUMN_ORDER = ['Compartment & Lux', '200s', '300s', '400s', '500s', 'No Room #', 'Day Board / Modified', 'Evaluation'];
function classifyPPColumn(dog) {
  // Evaluation dogs get their OWN section so staff never forget them — they sit
  // out sessions to acclimate before being re-flagged Large/Small/PP.
  if (dog.is_evaluation) return 'Evaluation';
  const rt = (dog.reservation_type || '').toString().toLowerCase().trim();
  // Day boarding / daycare must be checked BEFORE boarding, since "Day Boarding"
  // also contains the word "boarding".
  const isDayBoardOrDaycare =
    rt.startsWith('daycare') ||
    rt.startsWith('dayboard') ||
    rt.startsWith('day board') ||   // "day boarding", "day board", "day boarding/modified"
    rt.includes('day boarding');
  if (isDayBoardOrDaycare) return 'Day Board / Modified';

  // Everything else (boarding, or unknown reservation type) is placed by room #.
  const room = (dog.room || '').toString().trim();
  const r = room.toLowerCase();
  const compact = r.replace(/[^a-z0-9]/g, '');
  if (r.includes('compartment') || r.includes('lux') || compact.startsWith('sc') || compact.startsWith('dc')) return 'Compartment & Lux';
  const m = room.match(/(\d{3})/);
  if (m) {
    const n = parseInt(m[1], 10);
    if (n >= 100 && n < 200) return 'Compartment & Lux';
    if (n >= 200 && n < 300) return '200s';
    if (n >= 300 && n < 400) return '300s';
    if (n >= 400 && n < 500) return '400s';
    if (n >= 500 && n < 600) return '500s';
  }
  return 'No Room #';
}

function ppEscape(s) {
  return String(s || '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}

// Landscape column-grid print for one PP session (AM or PM). Columns fill the full page
// width; a few blank hand-write lines per column let staff add un-flagged dogs.
function printPPGrid(dogs, sessionLabel) {
  const win = window.open('', '_blank');
  const buckets = {};
  (dogs || []).forEach(d => {
    const col = classifyPPColumn(d);
    (buckets[col] = buckets[col] || []).push(d);
  });
  const sortByRoom = (arr) => [...arr].sort((a, b) => {
    const an = parseFloat((a.room || '').match(/\d+/)?.[0] ?? 'NaN');
    const bn = parseFloat((b.room || '').match(/\d+/)?.[0] ?? 'NaN');
    if (!isNaN(an) && !isNaN(bn)) return an - bn;
    return (a.room || 'zzz').localeCompare(b.room || 'zzz');
  });
  const cols = PP_COLUMN_ORDER.filter(c => (buckets[c] || []).length > 0);

  // Group dogs that share the SAME room into a single box (one room shown once,
  // names stacked) so staff instantly see they're a pair to take out together.
  const groupByRoom = (arr) => {
    const groups = [];
    const byRoom = {};
    arr.forEach(d => {
      const key = (d.room || '').trim();
      if (key && byRoom[key]) {
        byRoom[key].dogs.push(d);
      } else {
        const g = { room: key, dogs: [d] };
        if (key) byRoom[key] = g;
        groups.push(g);
      }
    });
    return groups;
  };

  const groupHtml = (g, lineNo) => {
    const pair = g.dogs.length > 1;
    const names = g.dogs
      .map(d => `<div class="nm">${ppEscape(d.animal_name) || '—'}</div>`)
      .join('');
    return `<div class="entry${pair ? ' pair' : ''}">`
      + `<span class="ln">${lineNo}</span>`
      + `<span class="rm">${ppEscape(g.room) || '—'}</span>`
      + `<div class="names">${names}${pair ? '<div class="pairtag">PAIR — out together</div>' : ''}</div>`
      + `</div>`;
  };
  const blankRow = (lineNo) => `<div class="entry blank"><span class="ln">${lineNo}</span><span class="rm">Rm ___</span><div class="names"><div class="nm">________________</div></div></div>`;
  const BLANK_LINES = 3; // spare hand-write lines at the bottom of every column

  const colHtml = cols.map(c => {
    const groups = groupByRoom(sortByRoom(buckets[c]));
    let n = 0;
    const rows = groups.map(g => groupHtml(g, ++n)).join('');
    const blanks = Array.from({ length: BLANK_LINES }, () => blankRow(++n)).join('');
    const evalCol = c === 'Evaluation' ? ' col-eval' : '';
    return `<div class="col${evalCol}"><div class="ch">${ppEscape(c)} <span class="cnt">(${buckets[c].length})</span></div>${rows}${blanks}</div>`;
  }).join('');

  win.document.write(`<html><head><title>Private Play — ${ppEscape(sessionLabel)}</title><style>
    @page{size:landscape;margin:0.35in}
    *{box-sizing:border-box}
    html,body{margin:0;padding:0}
    body{font-family:Arial,Helvetica,sans-serif;color:#111;padding:10px}
    .hdr{display:flex;justify-content:space-between;align-items:baseline;border-bottom:3px solid #111;padding-bottom:6px;margin-bottom:10px}
    .hdr h1{font-size:24px;margin:0;font-weight:800}
    .hdr .meta{font-size:14px;color:#555;font-weight:600}
    .grid{display:flex;gap:6px;align-items:flex-start}
    .col{flex:1;min-width:0;border:1.5px solid #333;border-radius:5px;overflow:hidden}
    .ch{background:#111;color:#fff;font-size:14px;font-weight:800;text-align:center;padding:6px 3px;text-transform:uppercase;letter-spacing:.4px}
    .ch .cnt{font-weight:600;opacity:.85}
    .entry{display:flex;align-items:flex-start;gap:6px;padding:8px;border-bottom:1px solid #999;break-inside:avoid}
    .entry:last-child{border-bottom:none}
    .ln{font-weight:800;font-size:13px;color:#fff;background:#444;border-radius:4px;min-width:20px;height:20px;line-height:20px;text-align:center;flex:0 0 auto}
    .rm{font-weight:800;font-size:16px;color:#111;white-space:nowrap;min-width:52px;flex:0 0 auto}
    .names{flex:1;min-width:0}
    .nm{font-weight:700;font-size:17px;line-height:1.2}
    .entry.pair{background:#fff7e6;border:2px solid #d98a00;border-radius:5px}
    .entry.pair .ln{background:#d98a00}
    .entry.pair .nm{padding:1px 0}
    .pairtag{font-size:10px;font-weight:800;color:#a35d00;text-transform:uppercase;letter-spacing:.4px;margin-top:2px}
    .entry.blank{height:34px}
    .entry.blank .ln{background:#ccc;color:#fff}
    .entry.blank .rm{color:#bbb}
    .entry.blank .nm{color:#ddd}
    .col-eval{border-color:#7c3aed}
    .col-eval .ch{background:#7c3aed}
  </style></head><body>
    <div class="hdr"><h1>Private Play — ${ppEscape(sessionLabel)} Session</h1>
      <span class="meta">${(dogs || []).length} dogs · ${new Date().toLocaleDateString()} · ${cols.length} sections</span></div>
    ${cols.length ? `<div class="grid">${colHtml}</div>` : '<div style="color:#999;font-size:18px;padding:20px">No dogs in this session.</div>'}
    <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 250); };</script>
  </body></html>`);
  win.document.close();
  win.focus();
}

// ─── Bath List Tab ────────────────────────────────────────────────────
function BathListTab() {
  const [baths, setBaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(localToday());
  // Date the currently-loaded `baths` actually belong to. Print uses this so the
  // header date can never disagree with the printed dog list (see bath print race fix).
  const [loadedDate, setLoadedDate] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'checkout_time', dir: 'asc' });
  const printRef = useRef(null);

  const fetchBaths = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    const fetchingDate = date;
    try {
      const res = await axios.get(`${API}/ops/bath-list?date=${date}`);
      setBaths(res.data.baths || []);
      setLoadedDate(fetchingDate);
    } catch (e) {
      if (silent) return;
      const status = e.response?.status;
      if (status === 401 || status === 403) {
        toast.error("Session expired — please log in again");
      } else {
        toast.error("Failed to load bath list");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [date]);

  useEffect(() => { fetchBaths(); }, [fetchBaths]);
  useEffect(() => {
    const id = setInterval(() => fetchBaths(true), 90000); // silent autorefresh keeps the cache warm
    return () => clearInterval(id);
  }, [fetchBaths]);

  const changeDate = (offset) => {
    const d = new Date(date + 'T12:00:00');
    d.setDate(d.getDate() + offset);
    setDate(d.toISOString().split('T')[0]);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortedBaths = (() => {
    if (!sortConfig.key) return baths;
    const sorted = [...baths].sort((a, b) => {
      let aVal = (a[sortConfig.key] || '').toString();
      let bVal = (b[sortConfig.key] || '').toString();
      // For checkout_time, convert 12-hour format to sortable 24-hour
      if (sortConfig.key === 'checkout_time') {
        const to24 = (t) => {
          const m = t.match(/(\d+):(\d+)\s*(AM|PM)/i);
          if (!m) return '99:99';
          let h = parseInt(m[1]);
          const min = m[2];
          const ampm = m[3].toUpperCase();
          if (ampm === 'PM' && h !== 12) h += 12;
          if (ampm === 'AM' && h === 12) h = 0;
          return `${String(h).padStart(2,'0')}:${min}`;
        };
        aVal = to24(aVal);
        bVal = to24(bVal);
      } else {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      return aVal.localeCompare(bVal);
    });
    return sortConfig.dir === 'desc' ? sorted.reverse() : sorted;
  })();

  const SortHeader = ({ label, sortKey, className }) => (
    <th className={`pb-3 pr-4 cursor-pointer select-none hover:text-gray-700 transition-colors ${className || ''}`} onClick={() => handleSort(sortKey)} data-testid={`bath-sort-${sortKey}`}>
      <div className="flex items-center gap-1">
        {label}
        {sortConfig.key === sortKey && (
          <span className="text-blue-500">{sortConfig.dir === 'asc' ? '\u2191' : '\u2193'}</span>
        )}
      </div>
    </th>
  );

  const isToday = date === localToday();
  const displayDate = new Date(date + 'T12:00:00');
  const dateLabel = isToday ? "Today" : displayDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  // Only safe to print once the loaded list matches the selected date (prevents a
  // stale header date printing against a freshly-swapped dog list).
  const printReady = !loading && loadedDate === date;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => changeDate(-1)} data-testid="bath-prev-day">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-[160px]" data-testid="bath-date-picker" />
          <Button variant="outline" size="icon" onClick={() => changeDate(1)} data-testid="bath-next-day">
            <ChevronRight className="h-4 w-4" />
          </Button>
          {!isToday && (
            <Button variant="ghost" size="sm" onClick={() => setDate(localToday())} className="text-xs">Today</Button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Badge variant="secondary" className="text-sm font-medium px-3 py-1" data-testid="bath-count">
            {baths.length} bath{baths.length !== 1 ? 's' : ''}
          </Badge>
          {(() => {
            const gc = { large: 0, small: 0, pp: 0 };
            baths.forEach(b => { if (b.group && gc[b.group] !== undefined) gc[b.group]++; });
            return (
              <div className="flex items-center gap-1.5" data-testid="bath-group-kpi">
                <Badge className="keep-color grp-lg text-xs font-bold px-2 hover:bg-[#F0FBF5]" data-testid="bath-kpi-large">Lg {gc.large}</Badge>
                <Badge className="keep-color grp-sm text-xs font-bold px-2 hover:bg-[#EFF6FF]" data-testid="bath-kpi-small">Sm {gc.small}</Badge>
                <Badge className="keep-color grp-pp text-xs font-bold px-2 hover:bg-[#FFF1F2]" data-testid="bath-kpi-pp">PP {gc.pp}</Badge>
              </div>
            );
          })()}
          <StandingBreakdownLine standing={baths.filter(b => b.is_standing).length} total={baths.length} label="baths" />
          <Button variant="outline" size="sm" onClick={() => { if (printReady) printBathList(baths, dateLabel, date); }} disabled={!printReady} title={printReady ? 'Print bath list' : 'Loading this date — please wait before printing'} data-testid="print-btn">
            <Printer className="h-4 w-4 mr-1" /> {printReady ? 'Print' : 'Loading…'}
          </Button>
          <Button variant="outline" size="icon" onClick={fetchBaths} disabled={loading} title="Refresh" aria-label="Refresh" className="h-9 w-9">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <div className="text-sm text-gray-500">{dateLabel}'s baths — dogs checking out with bath service</div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><RefreshCw className="h-8 w-8 animate-spin text-gray-400" /></div>
      ) : baths.length === 0 ? (
        <Card className="p-8 text-center">
          <Droplets className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No baths scheduled</p>
          <p className="text-sm text-gray-400 mt-1">No dogs checking out on this date have a bath service</p>
        </Card>
      ) : (
        <div ref={printRef}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="bath-list-table">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <SortHeader label="Dog" sortKey="animal_name" />
                  <SortHeader label="Room" sortKey="room" />
                  <SortHeader label="Group" sortKey="group" />
                  <SortHeader label="Bath Type" sortKey="bath_type" />
                  <SortHeader label="Res. Type" sortKey="reservation_type" />
                  <SortHeader label="Checkout" sortKey="checkout_time" />
                  <th className="pb-3 pr-4">Status</th>
                  <th className="pb-3 pr-4">Notes / Flags</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedBaths.map((bath, idx) => (
                  <tr key={`${bath.reservation_id}-${idx}`} className={`hover:bg-gray-50 transition-colors ${bath.is_completed ? 'opacity-50' : ''}`} data-testid={`bath-row-${bath.animal_id}`}>
                    <td className="py-2.5 pr-4">
                      <div className="flex items-center gap-2">
                        <div className="h-7 w-7 rounded-full overflow-hidden bg-blue-100 flex-shrink-0 flex items-center justify-center">
                          {bath.image_url ? (
                            <img src={bath.image_url} alt={bath.animal_name} className="h-full w-full object-cover dog-thumb" onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                          ) : null}
                          <Dog className="h-3.5 w-3.5 text-blue-400" style={bath.image_url ? { display: 'none' } : {}} />
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900 text-sm flex items-center gap-1.5">
                            {bath.animal_name}
                            <StandingBadge isStanding={bath.is_standing} size="xs" />
                          </div>
                          <div className="text-xs text-gray-500">{bath.breed}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-2.5 pr-4">
                      <EditableRoom value={bath.room} animalId={bath.animal_id} onSaved={(aid, room) => {
                        setBaths(prev => prev.map(b => b.animal_id === aid ? { ...b, room } : b));
                      }} />
                    </td>
                    <td className="py-2.5 pr-4">
                      {GROUP_META[bath.group] ? (
                        <Badge className={`${GROUP_META[bath.group].cls} text-xs font-bold`} title={GROUP_META[bath.group].label} data-testid={`bath-group-${bath.animal_id}`}>
                          {GROUP_META[bath.group].short}
                        </Badge>
                      ) : (
                        <span className="text-gray-400 text-xs" data-testid={`bath-group-${bath.animal_id}`}>—</span>
                      )}
                    </td>
                    <td className="py-2.5 pr-4">
                      <div className="flex flex-wrap items-center gap-1">
                        {(() => {
                          const t = (bath.bath_type || '').toLowerCase();
                          const styles = {
                            hypoallergenic: 'bg-amber-100 text-amber-800 border-amber-300 font-bold',
                            medicated: 'bg-rose-100 text-rose-800 border-rose-300 font-bold',
                            'no spray': 'bg-slate-100 text-slate-700 border-slate-300 font-semibold',
                            standard: 'bg-green-50 text-green-700 border-green-200',
                          };
                          const cls = styles[t] || 'bg-indigo-50 text-indigo-700 border-indigo-200';
                          return (
                            <Badge
                              className={`${cls} text-xs`}
                              data-testid={`bath-type-${t.replace(/\s+/g, '-') || 'standard'}`}
                            >
                              {bath.bath_type || 'Standard'}
                            </Badge>
                          );
                        })()}
                        {bath.is_fresh_n_clean && (
                          <Badge className="bg-cyan-100 text-cyan-800 border-cyan-300 font-semibold text-xs" data-testid="bath-type-fresh-n-clean">Fresh n Clean</Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-2.5 pr-4">
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                        {bath.reservation_type?.split('|')[0]?.trim() || 'Bath'}
                      </Badge>
                    </td>
                    <td className="py-2.5 pr-4">
                      <div className="flex items-center gap-1 text-gray-700">
                        <Clock className="h-3.5 w-3.5 text-gray-400" />{bath.checkout_time}
                      </div>
                    </td>
                    <td className="py-2.5 pr-4">
                      {bath.is_completed ? (
                        <Badge className="bg-green-100 text-green-700 border-green-200"><CheckCircle className="h-3 w-3 mr-1" /> Done</Badge>
                      ) : (
                        <Badge className="bg-amber-50 text-amber-700 border-amber-200"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>
                      )}
                    </td>
                    <td className="py-2.5 pr-4 max-w-[280px]">
                      <div className="flex flex-wrap gap-1">
                        {(bath.flags || []).map((flag, fi) => (
                          <span key={fi} className="text-xs text-indigo-700 bg-indigo-50 rounded px-1.5 py-0.5 truncate max-w-[200px]" title={flag} data-testid={`bath-flag-${bath.animal_id}-${fi}`}>{flag}</span>
                        ))}
                        {bath.medicines && <span className="text-xs text-amber-700 bg-amber-50 rounded px-1.5 py-0.5 truncate max-w-[120px]" title={bath.medicines}>Meds: {bath.medicines}</span>}
                        {bath.allergies && bath.allergies.toLowerCase() !== 'none' && <span className="text-xs text-red-700 bg-red-50 rounded px-1.5 py-0.5 truncate max-w-[120px]" title={bath.allergies}>Allergy: {bath.allergies}</span>}
                        {bath.reservation_notes && <span className="text-xs text-gray-600 bg-gray-50 rounded px-1.5 py-0.5 truncate max-w-[200px]" title={bath.reservation_notes}>{bath.reservation_notes}</span>}
                        {!(bath.flags?.length) && !bath.medicines && (!bath.allergies || bath.allergies.toLowerCase() === 'none') && !bath.reservation_notes && <span className="text-gray-400 text-xs">—</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Private Play Tab ─────────────────────────────────────────────────
function PrivatePlayTab() {
  const [data, setData] = useState({ am: [], pm: [] });
  const [loading, setLoading] = useState(true);
  const printRef = useRef(null);

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${API}/ops/private-play`);
      setData({ am: res.data.am || [], pm: res.data.pm || [] });
    } catch (e) {
      if (!silent) toast.error("Failed to load private play list");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const id = setInterval(() => fetchData(true), 90000); // silent autorefresh so newly checked-in dogs appear
    return () => clearInterval(id);
  }, [fetchData]);

  const PlayTable = ({ dogs, testId }) => {
    const sorted = [...dogs].sort((a, b) => (a.room || 'zzz').localeCompare(b.room || 'zzz'));
    return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid={testId}>
        <thead>
          <tr className="border-b border-gray-200 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
            <th className="pb-3 pr-4">Dog</th>
            <th className="pb-3 pr-4">Room</th>
            <th className="pb-3 pr-4">Type</th>
            <th className="pb-3 pr-4">Flag</th>
            <th className="pb-3 pr-4">Notes</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {sorted.map(dog => (
            <tr key={dog.animal_id} className="hover:bg-gray-50 transition-colors" data-testid={`pp-row-${dog.animal_id}`}>
              <td className="py-2 pr-4">
                <div className="flex items-center gap-2">
                  <div className="h-7 w-7 rounded-full overflow-hidden bg-purple-100 flex-shrink-0 flex items-center justify-center">
                    {dog.image_url ? (
                      <img src={dog.image_url} alt={dog.animal_name} className="h-full w-full object-cover dog-thumb" onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                    ) : null}
                    <Dog className="h-3.5 w-3.5 text-purple-400" style={dog.image_url ? { display: 'none' } : {}} />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 text-sm flex items-center gap-1.5">
                      {dog.animal_name}
                      <StandingBadge isStanding={dog.is_standing} size="xs" />
                    </div>
                    <div className="text-xs text-gray-500">{dog.breed}</div>
                  </div>
                </div>
              </td>
              <td className="py-2.5 pr-4">
                <EditableRoom value={dog.room} animalId={dog.animal_id} onSaved={(aid, room) => {
                  setData(prev => ({
                    ...prev,
                    am: prev.am.map(d => d.animal_id === aid ? { ...d, room } : d),
                    pm: prev.pm.map(d => d.animal_id === aid ? { ...d, room } : d),
                  }));
                }} />
              </td>
              <td className="py-2.5 pr-4">
                <Badge variant="secondary" className="text-xs">{dog.reservation_type}</Badge>
              </td>
              <td className="py-2.5 pr-4">
                {dog.pp_reason ? (
                  <span data-testid={`pp-flag-${dog.animal_id}`} title={(dog.flags || []).join(', ')}
                    className={`inline-flex items-center text-[11px] font-bold px-2 py-0.5 rounded-full border ${dog.is_evaluation ? 'bg-violet-100 border-violet-400 text-violet-800' : dog.is_true_pp ? 'bg-rose-50 border-rose-300 text-rose-700' : 'bg-amber-50 border-amber-300 text-amber-700'}`}>
                    {dog.is_evaluation ? `EVAL · ${dog.pp_reason}` : `${dog.pp_reason}${!dog.is_true_pp ? ' · not PP' : ''}`}
                  </span>
                ) : <span className="text-gray-400 text-xs">—</span>}
              </td>
              <td className="py-2.5 pr-4 max-w-[260px]">
                <div className="flex flex-wrap gap-1">
                  {dog.medicines && <span className="text-xs text-amber-700 bg-amber-50 rounded px-1.5 py-0.5 truncate max-w-[120px]" title={dog.medicines}>Meds: {dog.medicines}</span>}
                  {dog.allergies && <span className="text-xs text-red-700 bg-red-50 rounded px-1.5 py-0.5 truncate max-w-[120px]" title={dog.allergies}>Allergy: {dog.allergies}</span>}
                  {dog.reservation_notes && <span className="text-xs text-gray-600 bg-gray-50 rounded px-1.5 py-0.5 truncate max-w-[200px]" title={dog.reservation_notes}>{dog.reservation_notes}</span>}
                  {!dog.medicines && !dog.allergies && !dog.reservation_notes && <span className="text-gray-400 text-xs">—</span>}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16"><RefreshCw className="h-8 w-8 animate-spin text-gray-400" /></div>;
  }

  const total = data.am.length + data.pm.length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-sm text-gray-500">Today's private play + day boarding schedule ({total} total)</p>
          <StandingBreakdownLine
            standing={[...data.am, ...data.pm].filter(d => d.is_standing).length}
            total={total}
            label="dogs"
          />
        </div>
        <div className="flex items-center gap-2">
          {data.am.length > 0 && (
            <Button variant="outline" size="sm" onClick={() => printPPGrid(data.am, 'AM')} data-testid="pp-print-am-btn">
              <Printer className="h-4 w-4 mr-1" /> Print AM
            </Button>
          )}
          {data.pm.length > 0 && (
            <Button variant="outline" size="sm" onClick={() => printPPGrid(data.pm, 'PM')} data-testid="pp-print-pm-btn">
              <Printer className="h-4 w-4 mr-1" /> Print PM
            </Button>
          )}
          <Button variant="outline" size="icon" onClick={() => fetchData()} data-testid="pp-refresh" title="Refresh" aria-label="Refresh" className="h-9 w-9">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {total === 0 ? (
        <Card className="p-8 text-center">
          <Dog className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No private play dogs today</p>
        </Card>
      ) : (
        <div ref={printRef}>
          {/* AM Section (~10-11 AM) — Dogs already here */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="bg-amber-100 text-amber-800 font-bold text-sm px-3 py-1 rounded-full">AM</div>
              <span className="text-sm text-gray-500">{data.am.length} dog{data.am.length !== 1 ? 's' : ''} — Already here</span>
            </div>
            {data.am.length === 0 ? (
              <p className="text-sm text-gray-400 pl-4 mb-4">No AM private play today</p>
            ) : (
              <div className="mb-6" data-testid="pp-am-section">
                <PlayTable dogs={data.am} testId="pp-am-table" />
              </div>
            )}
          </div>

          {/* PM Section (~5 PM) — Checking in today */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="bg-indigo-100 text-indigo-800 font-bold text-sm px-3 py-1 rounded-full">PM</div>
              <span className="text-sm text-gray-500">{data.pm.length} dog{data.pm.length !== 1 ? 's' : ''} — Checking in today</span>
            </div>
            {data.pm.length === 0 ? (
              <p className="text-sm text-gray-400 pl-4">No PM private play today</p>
            ) : (
              <div data-testid="pp-pm-section">
                <PlayTable dogs={data.pm} testId="pp-pm-table" />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Room Management Tab ──────────────────────────────────────────────
function printRooms(dirtyRooms, conflicts, occupiedRooms, dateLabel) {
  const win = window.open('', '_blank');
  const sortByRoom = (items) => [...items].sort((a, b) => {
    const aNum = parseFloat(a.room || '999'); const bNum = parseFloat(b.room || '999');
    if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
    return (a.room || 'zzz').localeCompare(b.room || 'zzz');
  });
  // Rooms to Clean sorted by checkout time (matches Bath List / Going Home); occupied stay by room.
  const sortByCheckout = (items) => [...items].sort((a, b) => timeTo24(a.checkout_time).localeCompare(timeTo24(b.checkout_time)));
  const dirtyRows = sortByCheckout(dirtyRooms).map((r, i) =>
    `<tr><td class="rn">${i+1}</td><td class="room">${r.room || '—'}</td><td class="dog">${r.current_dog || '—'}</td><td>${r.owner || '—'}</td><td>${r.checkout_time || '—'}</td><td>${r.has_left ? 'Left' : r.status || 'Checking Out'}</td></tr>`
  ).join('');
  const occRows = sortByRoom(occupiedRooms).map((r, i) =>
    `<tr><td class="rn">${i+1}</td><td class="room">${r.room || '—'}</td><td class="dog">${r.current_dog || '—'}</td><td>${r.owner || '—'}</td><td>${r.reservation_type || '—'}</td></tr>`
  ).join('');
  win.document.write(`<html><head><title>Room Status</title><style>
    body{font-family:Arial,sans-serif;padding:16px;font-size:18px;line-height:1.4}
    h1{font-size:28px;margin:0 0 4px;font-weight:800}
    h2{font-size:18px;color:#666;margin:0 0 12px}
    table{width:100%;border-collapse:collapse}
    th{text-align:left;padding:8px 10px;border-bottom:3px solid #333;font-size:16px;text-transform:uppercase;color:#555}
    td{padding:8px 10px;border-bottom:2px solid #e0e0e0;font-size:20px}
    td.dog{font-weight:700;font-size:22px}
    td.room{font-weight:600;font-size:22px}
    td.rn{color:#aaa;font-size:16px;width:30px}
    .shdr{font-size:22px;font-weight:bold;margin:16px 0 6px;border-top:2px solid #ddd;padding-top:12px}
    .shdr:first-of-type{border-top:none;margin-top:0;padding-top:0}
    @media print{@page{margin:0.4in}}
  </style></head><body>
  <h1>Room Status — ${dateLabel}</h1><h2>${dirtyRooms.length} to clean | ${occupiedRooms.length} occupied</h2>
  ${dirtyRooms.length ? `<div class="shdr">Rooms to Clean (${dirtyRooms.length})</div><table><thead><tr><th>#</th><th>Room</th><th>Dog</th><th>Owner</th><th>Checkout</th><th>Status</th></tr></thead><tbody>${dirtyRows}</tbody></table>` : ''}
  ${occupiedRooms.length ? `<div class="shdr">Occupied Rooms (${occupiedRooms.length})</div><table><thead><tr><th>#</th><th>Room</th><th>Dog</th><th>Owner</th><th>Type</th></tr></thead><tbody>${occRows}</tbody></table>` : ''}
  </body></html>`);
  win.document.close();
  win.print();
}

function RoomsTab() {
  const [data, setData] = useState({ dirty_rooms: [], conflicts: [], occupied_rooms: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(localToday());
  const [sortConfig, setSortConfig] = useState({ key: 'checkout_time', dir: 'asc' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ops/rooms?date=${date}`);
      setData(res.data);
    } catch (e) {
      toast.error("Failed to load room data");
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const changeDate = (offset) => {
    const d = new Date(date + 'T12:00:00');
    d.setDate(d.getDate() + offset);
    setDate(d.toISOString().split('T')[0]);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortItems = (items) => {
    if (!sortConfig.key) return items;
    const sorted = [...items].sort((a, b) => {
      // Chronological sort for checkout time (matches Bath List / Going Home tabs)
      if (sortConfig.key === 'checkout_time') {
        const aT = timeTo24(a.checkout_time);
        const bT = timeTo24(b.checkout_time);
        return sortConfig.dir === 'desc' ? bT.localeCompare(aT) : aT.localeCompare(bT);
      }
      const aVal = (a[sortConfig.key] || '').toString().toLowerCase();
      const bVal = (b[sortConfig.key] || '').toString().toLowerCase();
      // Numeric sort for room numbers
      const aNum = parseFloat(aVal);
      const bNum = parseFloat(bVal);
      if (!isNaN(aNum) && !isNaN(bNum)) return sortConfig.dir === 'desc' ? bNum - aNum : aNum - bNum;
      return sortConfig.dir === 'desc' ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
    });
    return sorted;
  };

  const SortHeader = ({ label, sortKey }) => (
    <th className="pb-3 pr-4 cursor-pointer select-none hover:text-gray-700 transition-colors" onClick={() => handleSort(sortKey)} data-testid={`sort-${sortKey}`}>
      <div className="flex items-center gap-1">
        {label}
        {sortConfig.key === sortKey && (
          <span className="text-blue-500">{sortConfig.dir === 'asc' ? '\u2191' : '\u2193'}</span>
        )}
      </div>
    </th>
  );

  const isToday = date === localToday();
  const displayDate = new Date(date + 'T12:00:00');
  const dateLabel = isToday ? "Today" : displayDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });

  if (loading) {
    return <div className="flex items-center justify-center py-16"><RefreshCw className="h-8 w-8 animate-spin text-gray-400" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => changeDate(-1)} data-testid="rooms-prev-day">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-[160px]" data-testid="rooms-date-picker" />
          <Button variant="outline" size="icon" onClick={() => changeDate(1)} data-testid="rooms-next-day">
            <ChevronRight className="h-4 w-4" />
          </Button>
          {!isToday && (
            <Button variant="ghost" size="sm" onClick={() => setDate(localToday())} className="text-xs">Today</Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">{dateLabel}'s room status</span>
          <Button variant="outline" size="sm" onClick={() => printRooms(data.dirty_rooms, data.conflicts, data.occupied_rooms, dateLabel)} data-testid="rooms-print">
            <Printer className="h-4 w-4 mr-1" /> Print
          </Button>
          <Button variant="outline" size="icon" onClick={fetchData} data-testid="rooms-refresh" title="Refresh" aria-label="Refresh" className="h-9 w-9">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
          <div className="text-2xl font-bold text-amber-800" data-testid="dirty-count">{data.summary?.dirty_count || 0}</div>
          <div className="text-sm text-amber-600">Rooms to Clean</div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-red-50 to-rose-50 border-red-200">
          <div className="text-2xl font-bold text-red-800" data-testid="conflict-count">{data.summary?.conflict_count || 0}</div>
          <div className="text-sm text-red-600">Turnover Conflicts</div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <div className="text-2xl font-bold text-green-800" data-testid="occupied-count">{data.summary?.occupied_count || 0}</div>
          <div className="text-sm text-green-600">Currently Occupied</div>
        </Card>
      </div>

      {/* Turnover Conflicts */}
      {data.conflicts.length > 0 && (
        <div>
          <h3 className="font-semibold text-red-800 flex items-center gap-2 mb-3">
            <AlertTriangle className="h-5 w-5" /> Turnover Conflicts
          </h3>
          <div className="space-y-3" data-testid="conflict-list">
            {data.conflicts.map((c, idx) => (
              <Card key={idx} className="p-4 border-red-200 bg-red-50/50" data-testid={`conflict-${idx}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-red-100 text-red-700 border-red-200 font-bold">{c.room}</Badge>
                  <span className="text-xs text-red-500">{c.area}</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-3 border border-red-100">
                    <div className="text-xs font-semibold text-red-600 uppercase mb-1">Leaving</div>
                    <div className="font-medium text-gray-900">{c.outgoing_dog}</div>
                    <div className="text-sm text-gray-500">{c.outgoing_owner}</div>
                    <div className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                      <Clock className="h-3 w-3" /> {c.outgoing_time}
                      {c.outgoing_has_left && <Badge className="bg-green-100 text-green-700 text-[10px] ml-1">Left</Badge>}
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-red-100">
                    <div className="text-xs font-semibold text-blue-600 uppercase mb-1">Arriving</div>
                    <div className="font-medium text-gray-900">{c.incoming_dog}</div>
                    <div className="text-sm text-gray-500">{c.incoming_owner}</div>
                    <div className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                      <Clock className="h-3 w-3" /> {c.incoming_time}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Dirty Rooms */}
      {data.dirty_rooms.length > 0 && (
        <div>
          <h3 className="font-semibold text-amber-800 flex items-center gap-2 mb-3">
            <DoorOpen className="h-5 w-5" /> Rooms to Clean ({data.dirty_rooms.length})
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="dirty-rooms-table">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <SortHeader label="Room" sortKey="room" />
                  <SortHeader label="Dog" sortKey="current_dog" />
                  <SortHeader label="Owner" sortKey="owner" />
                  <SortHeader label="Checkout" sortKey="checkout_time" />
                  <th className="pb-3 pr-4">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortItems(data.dirty_rooms).map((room, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="py-3 pr-4">
                      <EditableRoom value={room.room} animalId={room.animal_id} onSaved={(aid, newRoom) => {
                        setData(prev => ({
                          ...prev,
                          dirty_rooms: prev.dirty_rooms.map(r =>
                            r.animal_id === aid ? { ...r, room: newRoom, is_exact_room: /\d/.test(newRoom) } : r
                          ),
                        }));
                      }} />
                      {room.reservation_type && (() => {
                        const subType = room.reservation_type.split('|').pop().trim().replace(/\s*\(.*?\)\s*$/, '');
                        return subType.toLowerCase() !== (room.room || '').toLowerCase() ? (
                          <div className="text-[10px] text-gray-400 mt-0.5">{subType}</div>
                        ) : null;
                      })()}
                    </td>
                    <td className="py-3 pr-4 text-gray-700">{room.current_dog}</td>
                    <td className="py-3 pr-4 text-gray-700">{room.owner}</td>
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-1 text-gray-700">
                        <Clock className="h-3.5 w-3.5 text-gray-400" />{room.checkout_time}
                      </div>
                    </td>
                    <td className="py-3 pr-4">
                      {room.has_left ? (
                        <Badge className="bg-green-100 text-green-700 border-green-200 text-xs"><CheckCircle className="h-3 w-3 mr-1" /> Left</Badge>
                      ) : (
                        <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs">{room.status || 'Checking Out Soon'}</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data.dirty_rooms.length === 0 && data.conflicts.length === 0 && (
        <Card className="p-8 text-center">
          <CheckCircle className="h-12 w-12 text-green-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">All clear! No rooms need attention</p>
        </Card>
      )}
    </div>
  );
}

// ─── Lodging Calendar ─────────────────────────────────────────────────
function LodgingCalendar({ selectedDate, onSelectDate }) {
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [month, setMonth] = useState(() => new Date().getMonth()); // 0-indexed
  const [data, setData] = useState([]);
  const [gingrUrl, setGingrUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMonth = useCallback(async () => {
    setLoading(true);
    const get = () => axios.get(`${API}/ops/lodging-calendar?month=${month + 1}&year=${year}`);
    try {
      let res;
      try { res = await get(); } catch { await new Promise((r) => setTimeout(r, 5000)); res = await get(); }
      setData(res.data?.days || []);
      setGingrUrl(res.data?.gingr_url || null);
    } catch {
      toast.error("Failed to load calendar data");
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { fetchMonth(); }, [fetchMonth]);

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  // Build lookup: date string -> day data
  const dayMap = {};
  data.forEach(d => { dayMap[d.date] = d; });

  // Compute averages for fire icon thresholds
  const daysWithData = data.filter(d => (d.check_ins || 0) + (d.check_outs || 0) > 0);
  const avgIn = daysWithData.length ? daysWithData.reduce((s, d) => s + (d.check_ins || 0), 0) / daysWithData.length : 0;
  const avgOut = daysWithData.length ? daysWithData.reduce((s, d) => s + (d.check_outs || 0), 0) / daysWithData.length : 0;
  const avgOvernight = daysWithData.length ? daysWithData.reduce((s, d) => s + (d.overnight || 0), 0) / daysWithData.length : 0;
  const avgBath = daysWithData.length ? daysWithData.reduce((s, d) => s + (d.bath || 0), 0) / daysWithData.length : 0;

  // Calendar grid
  const firstDow = new Date(year, month, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayStr = localToday();
  const monthLabel = new Date(year, month).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null); // leading blanks
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  // Build Gingr deep link for a given date
  const getGingrLink = (day) => {
    if (!gingrUrl) return null;
    // Gingr link format: https://{app_url}/reservations/by_time/{unix_timestamp}
    // Unix timestamp = midnight EST on that date
    const dt = new Date(year, month, day);
    const timestamp = Math.floor(dt.getTime() / 1000);
    return `https://${gingrUrl}/reservations/by_time/${timestamp}`;
  };

  return (
    <div className="space-y-4" data-testid="lodging-calendar">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900">{monthLabel}</h3>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={prevMonth} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={nextMonth} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <Button variant="outline" size="sm" className="text-xs" onClick={() => { setYear(new Date().getFullYear()); setMonth(new Date().getMonth()); }}>Today</Button>
          {onSelectDate && (
            <span className="hidden md:inline text-[11px] text-gray-400" data-testid="lodging-calendar-hint">
              Tip: click a day to load its lobby above · <span className="font-semibold text-gray-500">Gingr</span> button opens Gingr
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 sm:gap-4 text-[10px] sm:text-xs text-gray-500 flex-wrap">
          <div className="flex items-center gap-1"><LogIn className="h-3 w-3 text-emerald-600" /> In</div>
          <div className="flex items-center gap-1"><LogOut className="h-3 w-3 text-rose-500" /> Out</div>
          <div className="flex items-center gap-1"><BedDouble className="h-3 w-3 text-indigo-500" /> Stay</div>
          <div className="flex items-center gap-1"><ShowerHead className="h-3 w-3 text-sky-500" /> Bath</div>
          <div className="flex items-center gap-1"><Repeat className="h-3 w-3 text-violet-500" /> Standing</div>
          <div className="flex items-center gap-1"><Flame className="h-3 w-3 text-orange-500" /> Hot</div>
          {loading && <RefreshCw className="h-4 w-4 animate-spin text-gray-400" />}
        </div>
      </div>

      {/* Day-of-week header */}
      <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
        {DOW.map(d => (
          <div key={d} className="text-center text-[10px] sm:text-xs font-semibold text-gray-400 uppercase tracking-wider py-1">{d.charAt(0)}<span className="hidden sm:inline">{d.slice(1)}</span></div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-0.5 sm:gap-1.5">
        {cells.map((day, idx) => {
          if (day === null) return <div key={`blank-${idx}`} />;

          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const dd = dayMap[dateStr];
          const isToday = dateStr === todayStr;
          const isSelected = dateStr === selectedDate;
          const checkIn = dd?.check_ins || 0;
          const checkOut = dd?.check_outs || 0;
          const overnight = dd?.overnight || 0;
          const bath = dd?.bath || 0;
          const standingOvernight = dd?.standing_overnight || 0;
          const hasData = checkIn + checkOut + overnight + bath > 0;

          // Fire: any metric significantly above average (1.3x)
          const hotIn = checkIn > avgIn * 1.3 && checkIn > 0;
          const hotOut = checkOut > avgOut * 1.3 && checkOut > 0;
          const hotOvernight = overnight > avgOvernight * 1.3 && overnight > 0;
          const hotBath = bath > avgBath * 1.3 && bath > 0;
          const isHotDay = hotIn || hotOut || hotOvernight || hotBath;

          const dow = new Date(year, month, day).toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
          const gingrLink = getGingrLink(day);

          const cellContent = (
            <>
              {/* Date header */}
              <div className="flex items-baseline justify-between mb-1 sm:mb-2">
                <span className={`text-xs sm:text-lg font-bold ${isToday ? 'text-white' : 'text-gray-800'}`}>{day}</span>
                <div className="flex items-center gap-0.5">
                  <span className={`hidden sm:inline text-[10px] font-semibold ${isToday ? 'text-indigo-200' : 'text-gray-400'}`}>{dow}</span>
                  {isHotDay && <Flame className="hidden sm:block h-3 w-3 text-orange-400 animate-pulse" />}
                  {onSelectDate && gingrLink && (
                    <a href={gingrLink} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                      className={`hidden sm:inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-md border transition-colors ${isToday ? 'border-white/40 text-white hover:bg-white/20' : 'border-gray-200 text-gray-400 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-300'}`}
                      title="Open this day in Gingr" data-testid={`cal-gingr-${day}`}>
                      <span className="hidden lg:inline">Gingr</span><ExternalLink className="h-2.5 w-2.5" />
                    </a>
                  )}
                </div>
              </div>

              {hasData ? (
                <div className="space-y-0.5 sm:space-y-1">
                  {/* Mobile: compact 2-col grid | Desktop: full rows */}
                  <div className="hidden sm:block space-y-1">
                    <div className={`flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-xs font-semibold ${isToday ? 'bg-white/15' : 'bg-emerald-50'}`}>
                      <LogIn className={`h-3 w-3 ${isToday ? 'text-emerald-300' : 'text-emerald-500'}`} />
                      <span className={isToday ? 'text-emerald-200' : 'text-emerald-700'}>{checkIn}</span>
                      <span className={`text-[10px] font-medium ${isToday ? 'text-emerald-300/70' : 'text-emerald-500/60'}`}>IN</span>
                      {hotIn && <Flame className="h-2.5 w-2.5 text-orange-400 ml-auto" />}
                    </div>
                    <div className={`flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-xs font-semibold ${isToday ? 'bg-white/15' : 'bg-rose-50'}`}>
                      <LogOut className={`h-3 w-3 ${isToday ? 'text-rose-300' : 'text-rose-500'}`} />
                      <span className={isToday ? 'text-rose-200' : 'text-rose-700'}>{checkOut}</span>
                      <span className={`text-[10px] font-medium ${isToday ? 'text-rose-300/70' : 'text-rose-500/60'}`}>OUT</span>
                      {hotOut && <Flame className="h-2.5 w-2.5 text-orange-400 ml-auto" />}
                    </div>
                    <div className={`flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-xs font-semibold ${isToday ? 'bg-white/15' : 'bg-indigo-50'}`}>
                      <BedDouble className={`h-3 w-3 ${isToday ? 'text-indigo-300' : 'text-indigo-500'}`} />
                      <span className={isToday ? 'text-indigo-200' : 'text-indigo-700'}>{overnight}</span>
                      <span className={`text-[10px] font-medium ${isToday ? 'text-indigo-300/70' : 'text-indigo-500/60'}`}>STAY</span>
                      {hotOvernight && <Flame className="h-2.5 w-2.5 text-orange-400 ml-auto" />}
                    </div>
                    {bath > 0 && (
                      <div className={`flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-xs font-semibold ${isToday ? 'bg-white/15' : 'bg-sky-50'}`}>
                        <ShowerHead className={`h-3 w-3 ${isToday ? 'text-sky-300' : 'text-sky-500'}`} />
                        <span className={isToday ? 'text-sky-200' : 'text-sky-700'}>{bath}</span>
                        <span className={`text-[10px] font-medium ${isToday ? 'text-sky-300/70' : 'text-sky-500/60'}`}>BATH</span>
                      </div>
                    )}
                    {standingOvernight > 0 && (
                      <div
                        className={`flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-xs font-semibold ${isToday ? 'bg-white/15' : 'bg-violet-50'}`}
                        title={`${standingOvernight} standing reservation${standingOvernight !== 1 ? 's' : ''} staying tonight (recurring booking)`}
                        data-testid={`cal-standing-${dateStr}`}
                      >
                        <Repeat className={`h-3 w-3 ${isToday ? 'text-violet-300' : 'text-violet-500'}`} />
                        <span className={isToday ? 'text-violet-200' : 'text-violet-700'}>{standingOvernight}</span>
                        <span className={`text-[10px] font-medium ${isToday ? 'text-violet-300/70' : 'text-violet-500/60'}`}>STND</span>
                      </div>
                    )}
                  </div>
                  {/* Mobile: ultra compact */}
                  <div className="sm:hidden flex flex-col text-[10px] font-bold leading-[1.15] tabular-nums">
                    <span className={isToday ? 'text-emerald-200' : 'text-emerald-600'}>↓{checkIn}</span>
                    <span className={isToday ? 'text-rose-200' : 'text-rose-600'}>↑{checkOut}</span>
                    <span className={isToday ? 'text-indigo-200' : 'text-indigo-600'}>☾{overnight}</span>
                    {bath > 0 && (
                      <span className={`inline-flex items-center gap-px ${isToday ? 'text-sky-200' : 'text-sky-600'}`} data-testid={`cal-mobile-bath-${dateStr}`}>
                        <ShowerHead className="h-2.5 w-2.5" />{bath}
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <div className={`text-xs ${isToday ? 'text-white/40' : 'text-gray-300'}`}>—</div>
              )}
            </>
          );

          if (onSelectDate) {
            return (
              <div
                key={day}
                onClick={() => onSelectDate(dateStr)}
                role="button"
                tabIndex={0}
                className={`rounded-lg sm:rounded-xl border p-1 sm:p-2.5 min-h-[52px] sm:min-h-[110px] transition-all cursor-pointer ${
                  isToday
                    ? 'bg-indigo-500 border-indigo-400 text-white shadow-lg shadow-indigo-200 hover:bg-indigo-600'
                    : isSelected
                      ? 'bg-blue-50 border-blue-400 ring-2 ring-blue-400 shadow-md'
                      : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-md hover:bg-blue-50/30'
                }`}
                data-testid={`cal-day-${day}`}
                title="Click to see this day in Today's View"
              >
                {cellContent}
              </div>
            );
          }

          return gingrLink ? (
            <a
              key={day}
              href={gingrLink}
              target="_blank"
              rel="noopener noreferrer"
              className={`rounded-lg sm:rounded-xl border p-1 sm:p-2.5 min-h-[52px] sm:min-h-[110px] transition-all cursor-pointer ${
                isToday
                  ? 'bg-indigo-500 border-indigo-400 text-white shadow-lg shadow-indigo-200 hover:bg-indigo-600'
                  : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-md hover:bg-blue-50/30'
              }`}
              data-testid={`cal-day-${day}`}
              title="Click to view in Gingr"
            >
              {cellContent}
            </a>
          ) : (
            <div
              key={day}
              className={`rounded-lg sm:rounded-xl border p-1 sm:p-2.5 min-h-[52px] sm:min-h-[110px] transition-all ${
                isToday
                  ? 'bg-indigo-500 border-indigo-400 text-white shadow-lg shadow-indigo-200'
                  : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
              }`}
              data-testid={`cal-day-${day}`}
            >
              {cellContent}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Forecast Calendar (Standing + Confirmed + Walk-in Forecast) ──────
function ForecastCalendar() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const [days, setDays] = useState(28); // 4 weeks default
  const [service, setService] = useState('daycare'); // 'daycare' | 'boarding' | 'all'

  const fetchForecast = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ops/forecast?days=${days}`);
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load forecast');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchForecast(); }, [days]);

  const recompute = async () => {
    setRecomputing(true);
    try {
      const res = await axios.post(`${API}/ops/forecast/recompute`);
      toast.success(`Baseline refreshed — ${res.data.sample_total} reservations over ${res.data.lookback_weeks} weeks`);
      await fetchForecast();
    } catch (e) {
      toast.error('Failed to recompute baseline');
    } finally {
      setRecomputing(false);
    }
  };

  // Group days into weeks for the calendar view
  const weeks = useMemo(() => {
    if (!data?.days) return [];
    const result = [];
    let cur = [];
    data.days.forEach((d) => {
      if (cur.length === 0) {
        // Pad start of first week with blanks based on its DOW (Sun=0 alignment but our dow is Mon=0)
        const date = new Date(d.date + 'T12:00:00');
        const jsDow = date.getDay(); // 0=Sun..6=Sat
        const padStart = jsDow === 0 ? 6 : jsDow - 1; // align to Mon-start
        for (let i = 0; i < padStart; i++) cur.push(null);
      }
      cur.push(d);
      const date = new Date(d.date + 'T12:00:00');
      if (date.getDay() === 0) { // Sunday ends a Mon-start week
        result.push(cur);
        cur = [];
      }
    });
    if (cur.length) {
      while (cur.length < 7) cur.push(null);
      result.push(cur);
    }
    return result;
  }, [data]);

  // Aggregate totals across the visible window for a header summary
  const totals = useMemo(() => {
    if (!data?.days) return null;
    const sum = { dc_confirmed: 0, dc_low: 0, dc_mid: 0, dc_high: 0, bd_confirmed: 0, bd_low: 0, bd_mid: 0, bd_high: 0, standing_dc: 0, standing_bd: 0 };
    data.days.forEach((d) => {
      if (d.is_past) return;
      const dc = d.services.daycare, bd = d.services.boarding;
      sum.dc_confirmed += dc.confirmed; sum.dc_low += dc.total_low; sum.dc_mid += dc.total_mid; sum.dc_high += dc.total_high;
      sum.bd_confirmed += bd.confirmed; sum.bd_low += bd.total_low; sum.bd_mid += bd.total_mid; sum.bd_high += bd.total_high;
      sum.standing_dc += dc.standing; sum.standing_bd += bd.standing;
    });
    return sum;
  }, [data]);

  if (loading && !data) {
    return <div className="flex items-center justify-center py-12 text-gray-500"><RefreshCw className="h-5 w-5 animate-spin mr-2" /> Loading forecast…</div>;
  }

  if (!data || !data.days.length) {
    return <div className="text-center py-12 text-gray-500">No forecast data available.</div>;
  }

  const lastComputed = data.baseline_last_computed ? new Date(data.baseline_last_computed).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

  const renderServiceCell = (d, svc) => {
    if (!d) return null;
    const s = d.services[svc];
    if (!s) return null;
    const color = svc === 'daycare' ? 'sky' : 'amber';
    const label = svc === 'daycare' ? 'DC' : 'BD';
    const totalRange = s.total_low === s.total_high ? `${s.total_mid}` : `${s.total_low}–${s.total_high}`;
    return (
      <div
        className={`rounded-md px-1.5 py-1 text-[10px] sm:text-xs bg-${color}-50`}
        title={`${svc} on ${d.date}: ${s.confirmed} confirmed + forecast ${s.forecast_walkins_low}-${s.forecast_walkins_high} walk-ins → total ${s.total_low}-${s.total_high} (mid ${s.total_mid})`}
      >
        <div className="flex items-center justify-between gap-1 leading-tight">
          <span className={`font-bold text-${color}-700`}>{label}</span>
          <span className="text-gray-600 font-medium">{s.confirmed} <span className="text-[8px] sm:text-[9px] text-gray-400 font-normal">conf</span></span>
        </div>
        <div className="flex items-center justify-between gap-1 leading-tight">
          <span className={`text-${color}-600/80`}>+{s.forecast_walkins_low}-{s.forecast_walkins_high}</span>
          <span className="text-[8px] sm:text-[9px] text-gray-400 font-normal">walk</span>
        </div>
        <div className={`flex items-center justify-between gap-1 leading-tight mt-0.5 pt-0.5 border-t border-${color}-200`}>
          <span className={`text-[8px] sm:text-[9px] uppercase tracking-wide text-${color}-700/70 font-semibold`}>Total</span>
          <span className={`font-bold text-${color}-900 text-[11px] sm:text-sm`}>{totalRange}</span>
        </div>
      </div>
    );
  };

  const DOW_HDR = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
  const todayStr = localToday();

  return (
    <div className="space-y-4" data-testid="forecast-calendar">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-violet-600" />
            Forecast — Confirmed + Predicted Walk-Ins
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            12-week baseline from {data.baseline_window_start} → {data.baseline_window_end} ({data.sample_total} reservations) · refreshed {lastComputed}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {[7, 14, 28].map(n => (
            <Button key={n} variant={days === n ? 'default' : 'outline'} size="sm" className="text-xs h-7 px-2" onClick={() => setDays(n)} data-testid={`forecast-days-${n}`}>
              {n}d
            </Button>
          ))}
          <Button variant="outline" size="sm" className="text-xs h-7 px-2 gap-1" onClick={recompute} disabled={recomputing} data-testid="forecast-recompute">
            <RefreshCw className={`h-3 w-3 ${recomputing ? 'animate-spin' : ''}`} /> Recompute
          </Button>
        </div>
      </div>

      {/* Window totals summary */}
      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3" data-testid="forecast-totals">
          <Card className="p-3 bg-gradient-to-br from-sky-50 to-blue-50 border-sky-200">
            <p className="text-[10px] sm:text-xs font-semibold text-sky-700 uppercase tracking-wider">Daycare · Next {days}d</p>
            <p className="text-xl sm:text-2xl font-bold text-sky-900 mt-1">{totals.dc_low}–{totals.dc_high}</p>
            <p className="text-[10px] text-sky-600 mt-0.5">Confirmed {totals.dc_confirmed} · Standing {totals.standing_dc}</p>
          </Card>
          <Card className="p-3 bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
            <p className="text-[10px] sm:text-xs font-semibold text-amber-700 uppercase tracking-wider">Boarding · Next {days}d</p>
            <p className="text-xl sm:text-2xl font-bold text-amber-900 mt-1">{totals.bd_low}–{totals.bd_high}</p>
            <p className="text-[10px] text-amber-600 mt-0.5">Confirmed {totals.bd_confirmed} · Standing {totals.standing_bd}</p>
          </Card>
          <Card className="p-3 bg-gradient-to-br from-violet-50 to-fuchsia-50 border-violet-200">
            <p className="text-[10px] sm:text-xs font-semibold text-violet-700 uppercase tracking-wider">Expected Mid Estimate</p>
            <p className="text-xl sm:text-2xl font-bold text-violet-900 mt-1">{totals.dc_mid + totals.bd_mid}</p>
            <p className="text-[10px] text-violet-600 mt-0.5">DC {totals.dc_mid} + BD {totals.bd_mid}</p>
          </Card>
          <Card className="p-3 bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
            <p className="text-[10px] sm:text-xs font-semibold text-emerald-700 uppercase tracking-wider">Range Total</p>
            <p className="text-xl sm:text-2xl font-bold text-emerald-900 mt-1">{totals.dc_low + totals.bd_low}–{totals.dc_high + totals.bd_high}</p>
            <p className="text-[10px] text-emerald-600 mt-0.5">Low → High envelope</p>
          </Card>
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-3 sm:gap-4 text-[10px] sm:text-xs text-gray-500 flex-wrap">
        <span className="font-semibold text-gray-700">Per-day cell:</span>
        <div className="flex items-center gap-1"><span className="font-bold text-sky-700">DC</span> Daycare</div>
        <div className="flex items-center gap-1"><span className="font-bold text-amber-700">BD</span> Boarding</div>
        <div className="flex items-center gap-1"><span className="text-gray-700 font-semibold">conf</span> = confirmed (already in Gingr)</div>
        <div className="flex items-center gap-1"><span className="text-sky-600">walk</span> = predicted walk-in range</div>
        <div className="flex items-center gap-1"><span className="font-bold">Total</span> = low–high envelope</div>
      </div>

      {/* Day-of-week header */}
      <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
        {DOW_HDR.map(d => (
          <div key={d} className="text-center text-[10px] sm:text-xs font-semibold text-gray-400 uppercase tracking-wider py-1">{d}</div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="space-y-1 sm:space-y-1.5">
        {weeks.map((week, wi) => (
          <div key={wi} className="grid grid-cols-7 gap-0.5 sm:gap-1.5">
            {week.map((d, di) => {
              if (!d) return <div key={`blank-${wi}-${di}`} />;
              const isToday = d.date === todayStr;
              return (
                <div
                  key={d.date}
                  className={`rounded-lg p-1.5 sm:p-2 border min-h-[120px] ${isToday ? 'bg-gradient-to-br from-indigo-600 to-violet-600 text-white border-indigo-700 shadow-lg' : d.is_past ? 'bg-gray-50 border-gray-200 opacity-60' : 'bg-white border-gray-200'}`}
                  data-testid={`forecast-day-${d.date}`}
                >
                  <div className="flex items-baseline justify-between mb-1">
                    <span className={`text-xs sm:text-sm font-bold ${isToday ? 'text-white' : 'text-gray-800'}`}>{parseInt(d.date.slice(-2), 10)}</span>
                    <span className={`text-[9px] sm:text-[10px] font-semibold ${isToday ? 'text-indigo-200' : 'text-gray-400'}`}>{d.dow.toUpperCase()}</span>
                  </div>
                  <div className="space-y-1">
                    {renderServiceCell(d, 'daycare')}
                    {renderServiceCell(d, 'boarding')}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div className="text-[11px] text-gray-500 italic">
        Forecast based on ±1 stddev of {data.lookback_weeks}-week historical walk-in (same-day) + late-booking (within-24h) patterns by day-of-week. Recompute runs automatically every 24h.
      </div>
    </div>
  );
}

// ─── Today's View (hourly lobby timeline) ─────────────────────────────
function DogChips({ dogs, max, tone, doneKey }) {
  if (!dogs || dogs.length === 0) return null;
  // Surface still-pending dogs first (who's left to check in / go home), arrived ones after.
  const ordered = doneKey ? [...dogs].sort((a, b) => (a[doneKey] ? 1 : 0) - (b[doneKey] ? 1 : 0)) : dogs;
  const shown = ordered.slice(0, max);
  const extra = ordered.length - shown.length;
  const cls = {
    g: "bg-green-100 text-green-800",
    b: "bg-blue-100 text-blue-800",
    a: "bg-amber-100 text-amber-800",
  }[tone] || "bg-gray-100 text-gray-700";
  return (
    <span className="inline-flex flex-wrap gap-1 ml-2 align-middle">
      {shown.map((d, i) => {
        const done = doneKey && d[doneKey];
        return (
          <span key={i}
            className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${cls} ${done ? "opacity-40 line-through" : ""}`}
            title={`${d.name}${d.room ? " · " + d.room : ""} · ${d.time}${done ? " · done" : ""}`}>
            {d.name}{d.is_standing ? " ↻" : ""}
          </span>
        );
      })}
      {extra > 0 && (
        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${cls} opacity-80`}>+{extra}</span>
      )}
    </span>
  );
}

const LOAD_STYLE = [
  { w: "6%", bg: "#cbd5e1" },
  { w: "28%", bg: "#86efac" },
  { w: "55%", bg: "#fbbf24" },
  { w: "100%", bg: "#f43f5e" },
];

function Cnt({ n, tone }) {
  if (!n) return <span className="inline-flex items-center justify-center min-w-[26px] h-6 rounded-lg font-bold text-[13px] px-1.5 bg-gray-100 text-gray-300">0</span>;
  const cls = {
    g: "bg-green-100 text-green-700",
    b: "bg-blue-100 text-blue-700",
    a: "bg-amber-100 text-amber-700",
  }[tone];
  return <span className={`inline-flex items-center justify-center min-w-[26px] h-6 rounded-lg font-bold text-[13px] px-1.5 ${cls}`}>{n}</span>;
}

// KPI group split — Boarding/Daycare × Large/Small/Private-Play pills
function GroupRow({ label, data, testId }) {
  const d = data || {};
  return (
    <div className="flex flex-wrap items-center gap-1 sm:gap-1.5" data-testid={testId}>
      <span className="text-[11px] font-bold text-gray-500 w-full shrink-0">{label} <span className="text-gray-800">{d.total || 0}</span></span>
      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full keep-color grp-lg">Lg {d.large || 0}</span>
      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full keep-color grp-sm">Sm {d.small || 0}</span>
      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full keep-color grp-pp">PP {d.pp || 0}</span>
    </div>
  );
}

const TONE_INK = { neg: "text-[#C2483A]", warn: "text-[#9A6B00]", pos: "text-[#2F8F5F]" };

function KpiCard({ icon, label, value, tone, breakdown, footer, testId, testIdPrefix, className = "", onClick }) {
  return (
    <div className={`border border-[#1F8A7C]/25 bg-[#E6EFEF] rounded-xl px-3 sm:px-4 py-3 min-w-0 transition-colors hover:border-[#1F8A7C]/40 ${tone ? `v3-soft ${tone}` : ""} ${className} ${onClick ? "cursor-pointer" : ""}`}
      data-testid={testId} onClick={onClick} role={onClick ? "button" : undefined}>
      <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wide flex items-center gap-1.5 [&_svg]:text-gray-400">{icon} {label}</div>
      <div className={`v3-num text-2xl sm:text-[28px] font-bold leading-tight ${TONE_INK[tone] || "text-gray-900"} mt-0.5`}>{value}</div>
      {breakdown && (
        <div className="mt-2 pt-2 border-t border-gray-200/70 space-y-1">
          <GroupRow label="Board" data={breakdown.boarding} testId={`${testIdPrefix}-boarding`} />
          <GroupRow label="Daycare" data={breakdown.daycare} testId={`${testIdPrefix}-daycare`} />
        </div>
      )}
      {footer}
    </div>
  );
}

function useIsNarrow(bp = 640) {
  const [n, setN] = useState(() => typeof window !== "undefined" && window.innerWidth < bp);
  useEffect(() => {
    const on = () => setN(window.innerWidth < bp);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, [bp]);
  return n;
}

// Phone layout: one stacked card per busy half-hour instead of a 5-column table.
function MobileTimeline({ slots, isToday, nowMin }) {
  const busy = slots.filter((s) => s.arrivals.count + s.departures.count + s.baths.count > 0);
  const Row = ({ label, n, tone, dogs, max, doneKey }) => n ? (
    <div className="flex items-start gap-2 mt-1.5">
      <span className="text-[10px] font-bold uppercase text-gray-400 w-9 shrink-0 pt-1">{label}</span>
      <Cnt n={n} tone={tone} /><DogChips dogs={dogs} max={max} tone={tone} doneKey={doneKey} />
    </div>
  ) : null;
  return (
    <div className="sm:hidden space-y-2" data-testid="todays-view-timeline-mobile">
      {busy.map((s) => {
        const ls = LOAD_STYLE[s.load];
        const isNow = isToday && nowMin != null && nowMin >= s.minutes && nowMin < s.minutes + 30;
        const isPast = isToday && nowMin != null && s.minutes + 30 <= nowMin;
        return (
          <div key={s.minutes} className={`rounded-xl border px-3 py-2 ${isNow ? "border-indigo-400 bg-indigo-50/70" : s.load === 3 ? "border-rose-200 bg-rose-50/60" : "border-gray-100"} ${isPast ? "opacity-55" : ""}`} data-testid={`todays-view-mslot-${s.minutes}`}>
            <div className="flex items-center gap-2">
              <span className={`font-extrabold tabular-nums text-sm ${isNow ? "text-indigo-600" : s.load === 3 ? "text-rose-600" : "text-gray-900"}`}>{s.time}</span>
              {isNow && <span className="text-[9px] font-extrabold bg-indigo-600 text-white px-1.5 py-0.5 rounded-full">NOW</span>}
              <div className="ml-auto w-20 h-2 rounded-md bg-gray-100 overflow-hidden"><div className="h-full rounded-md" style={{ width: ls.w, background: ls.bg }} /></div>
            </div>
            <Row label="In" n={s.arrivals.count} tone="g" dogs={s.arrivals.dogs} max={8} doneKey={isToday ? "arrived" : null} />
            <Row label="Out" n={s.departures.count} tone="b" dogs={s.departures.dogs} max={8} doneKey={isToday ? "departed" : null} />
            <Row label="Bath" n={s.baths.count} tone="a" dogs={s.baths.dogs} max={6} />
          </div>
        );
      })}
    </div>
  );
}

function TodaysView({ selectedDate, onResetToday }) {
  const todayStr = localToday();
  const effDate = selectedDate || todayStr;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [layout, setLayout] = useState("timeline");
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const res = await axios.get(`${API}/ops/todays-view?date=${effDate}`);
      setData(res.data);
    } catch (e) {
      if (!silent) toast.error("Failed to load Today's View");
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, [effDate]);
  useEffect(() => { fetchData(); }, [fetchData]);
  // Live current-day dashboard: silently refresh every 90s while viewing today
  // so the KPIs track Gingr's real-time check-in/out state (matches server cache TTL).
  useEffect(() => {
    if (effDate !== todayStr) return;
    const id = setInterval(() => fetchData(true), 90000);
    return () => clearInterval(id);
  }, [effDate, todayStr, fetchData]);

  const summary = data?.summary || {};
  const liveMode = !!data?.is_today;
  const [showInBuilding, setShowInBuilding] = useState(false);
  const arrValue = liveMode ? (summary.arrivals_live ?? 0) : (summary.arrivals ?? 0);
  const arrBreakdown = liveMode ? summary.breakdown_live?.arrivals : summary.breakdown?.arrivals;
  const arrArrived = (summary.arrivals ?? 0) - (summary.arrivals_remaining ?? 0);
  const depValue = liveMode ? (summary.departures_live ?? 0) : (summary.departures ?? 0);
  const depBreakdown = liveMode ? summary.breakdown_live?.departures : summary.breakdown?.departures;
  const depGone = (summary.departures ?? 0) - (summary.departures_remaining ?? 0);
  const slots = data?.slots || [];
  const peak = data?.peak_departure;
  const peakArr = data?.peak_arrival;
  const isToday = !!data?.is_today;
  const nowMin = data?.now_minutes;
  const dateLabel = data?.date
    ? new Date(data.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })
    : "";

  // Flatten for the histogram list view
  const maxCount = Math.max(1, ...slots.map(s => Math.max(s.arrivals.count, s.departures.count)));

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-3 sm:p-5 shadow-sm min-w-0" data-testid="todays-view">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <OpsSectionHead icon={LayoutGrid} title={isToday ? "Today’s View" : "Day View"} sub="Lobby by the half hour — check-ins, going home and baths" />
        <div className="flex flex-wrap items-center gap-3">
          {data && !isToday && (
            <button onClick={onResetToday} data-testid="todays-view-jump-today-btn"
              className="text-xs font-bold px-3 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700">
              Jump to today
            </button>
          )}
          {isToday && (
            <span className="inline-flex items-center gap-1.5 text-[10px] font-extrabold text-green-600 uppercase tracking-wide" data-testid="todays-view-live-badge">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
              </span>
              Live
            </span>
          )}
          <span className="text-xs text-gray-500 font-semibold" data-testid="todays-view-date">{dateLabel}</span>
          <div className="inline-flex border border-gray-200 rounded-lg overflow-hidden" data-testid="todays-view-layout-toggle">
            <button onClick={() => setLayout("timeline")} data-testid="todays-view-timeline-btn"
              className={`text-xs font-bold px-3 py-1.5 ${layout === "timeline" ? "bg-gray-900 text-white" : "bg-white text-gray-500"}`}>Timeline</button>
            <button onClick={() => setLayout("histogram")} data-testid="todays-view-histogram-btn"
              className={`text-xs font-bold px-3 py-1.5 ${layout === "histogram" ? "bg-gray-900 text-white" : "bg-white text-gray-500"}`}>Histogram</button>
          </div>
          <button onClick={() => fetchData(true)} data-testid="todays-view-refresh-btn" title="Refresh" className="h-8 w-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50">
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-gray-400 text-sm" data-testid="todays-view-loading">Loading today&rsquo;s lobby…</div>
      ) : data?.error ? (
        <div className="py-10 text-center text-gray-400 text-sm">{data.error}</div>
      ) : (
        <>
          {/* Peak staffing callout */}
          {peak && (
            <div className="flex gap-3 items-start bg-rose-50 border border-rose-200 border-l-[3px] border-l-rose-500 rounded-lg px-4 py-2 mb-4" data-testid="todays-view-peak-callout">
              <AlertTriangle className="h-4 w-4 text-rose-500 shrink-0 mt-0.5" />
              <div className="text-sm">
                <span className="font-bold text-rose-600">Heaviest departures {peak.start}–{peak.end} — {peak.count} dogs going home.</span>
                <div className="text-gray-500 text-xs mt-0.5">
                  All hands on the floor, hold breaks &amp; non-essential cleaning.
                  {peakArr ? ` Busiest arrival block: ${peakArr.start}–${peakArr.end} (${peakArr.count} in).` : ""}
                </div>
              </div>
            </div>
          )}

          {/* Summary stats */}
          <div className={`grid grid-cols-2 gap-2 sm:gap-3 mb-3 ${liveMode ? "md:grid-cols-6 lg:grid-cols-5" : "lg:grid-cols-4"}`}>
            <KpiCard className={liveMode ? "md:col-span-2 lg:col-span-1" : ""}
              testId="todays-view-stat-arrivals" testIdPrefix="todays-view-arrivals"
              icon={<LogIn className="h-3.5 w-3.5" />} label={liveMode ? "Still to check in" : "Checking in"}
              value={arrValue} tone={liveMode && arrValue > 0 && nowMin >= 600 ? "warn" : undefined}
              breakdown={arrBreakdown}
              footer={liveMode && (
                <div className="text-[11px] font-semibold text-gray-500 mt-2" data-testid="todays-view-arrivals-remaining">
                  {arrValue > 0 ? `${arrArrived} of ${summary.arrivals ?? 0} checked in` : "All checked in ✓"}
                </div>
              )}
            />
            {liveMode && (
              <KpiCard className="md:col-span-2 lg:col-span-1" onClick={() => setShowInBuilding(true)}
                testId="todays-view-stat-inbuilding" testIdPrefix="todays-view-inbuilding"
                icon={<Dog className="h-3.5 w-3.5" />} label="Checked in now"
                value={summary.in_building_live ?? 0}
                breakdown={summary.breakdown_live?.in_building}
                footer={<div className="text-[11px] font-semibold text-[#1F8A7C] mt-2">In the building right now · tap for list</div>}
              />
            )}
            <KpiCard className={liveMode ? "md:col-span-2 lg:col-span-1" : ""}
              testId="todays-view-stat-departures" testIdPrefix="todays-view-departures"
              icon={<LogOut className="h-3.5 w-3.5" />} label={liveMode ? "Going home now" : "Going home"}
              value={depValue}
              breakdown={depBreakdown}
              footer={liveMode && (
                <div className="text-[11px] font-semibold text-gray-500 mt-2" data-testid="todays-view-departures-remaining">
                  {`${depGone} of ${summary.departures ?? 0} gone home`}
                </div>
              )}
            />
            <KpiCard className={liveMode ? "md:col-span-3 lg:col-span-1" : ""}
              testId="todays-view-stat-baths" testIdPrefix="todays-view-baths"
              icon={<ShowerHead className="h-3.5 w-3.5" />} label="Baths"
              value={summary.baths ?? 0}
              breakdown={summary.breakdown?.baths}
            />
            <KpiCard className={liveMode ? "col-span-2 md:col-span-3 lg:col-span-1" : ""}
              testId="todays-view-stat-overnight" testIdPrefix="todays-view-overnight"
              icon={<BedDouble className="h-3.5 w-3.5" />} label="Overnight"
              value={summary.overnight ?? 0}
              breakdown={summary.breakdown?.overnight}
            />
          </div>
          <InBuildingDialog open={showInBuilding} onOpenChange={setShowInBuilding} dogs={summary.in_building_dogs || []} />
          <div className="flex items-center gap-3 flex-wrap text-[11px] text-gray-500 mb-5" data-testid="todays-view-group-legend">
            <span className="font-semibold text-gray-600">Groups:</span>
            <span className="flex items-center gap-1"><span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full keep-color grp-lg">Lg</span> Large playgroup</span>
            <span className="flex items-center gap-1"><span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full keep-color grp-sm">Sm</span> Small playgroup</span>
            <span className="flex items-center gap-1"><span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full keep-color grp-pp">PP</span> Private Play (flag-based)</span>
            {liveMode && <span className="text-green-600 font-semibold">· Live: Still to check in, Checked in now &amp; Going home reflect current check-in/out (auto-refreshes)</span>}
          </div>

          {slots.length === 0 ? (
            <div className="py-10 text-center text-gray-400 text-sm">No check-ins, check-outs or baths scheduled for this day.</div>
          ) : layout === "timeline" ? (
            <div className="overflow-x-auto keep-color">
              <MobileTimeline slots={slots} isToday={isToday} nowMin={nowMin} />
              <table className="hidden sm:table w-full text-sm border-collapse" data-testid="todays-view-timeline">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wide text-gray-400">
                    <th className="text-left font-semibold pb-2 px-2.5 w-[78px]">Time</th>
                    <th className="text-left font-semibold pb-2 px-2.5 w-[120px]">Lobby load</th>
                    <th className="text-left font-semibold pb-2 px-2.5">Checking in</th>
                    <th className="text-left font-semibold pb-2 px-2.5">Going home</th>
                    <th className="text-left font-semibold pb-2 px-2.5">Baths</th>
                  </tr>
                </thead>
                <tbody>
                  {slots.map((s) => {
                    const ls = LOAD_STYLE[s.load];
                    const isPeak = s.load === 3;
                    const isNow = isToday && nowMin != null && nowMin >= s.minutes && nowMin < s.minutes + 30;
                    const isPast = isToday && nowMin != null && s.minutes + 30 <= nowMin;
                    return (
                      <tr key={s.minutes}
                        className={`border-b border-gray-50 ${isNow ? "bg-indigo-50/70 border-l-2 border-l-indigo-500" : isPeak ? "bg-rose-50/60" : ""} ${isPast ? "opacity-55" : ""}`}
                        data-testid={`todays-view-slot-${s.minutes}`}>
                        <td className={`py-2 px-2.5 font-extrabold tabular-nums whitespace-nowrap ${isNow ? "text-indigo-600" : isPeak ? "text-rose-600" : "text-gray-900"}`}>
                          {s.time}
                          {isNow && <span className="ml-1.5 text-[9px] font-extrabold bg-indigo-600 text-white px-1.5 py-0.5 rounded-full align-middle" data-testid="todays-view-now-marker">NOW</span>}
                        </td>
                        <td className="py-2 px-2.5">
                          <div className="h-2.5 rounded-md bg-gray-100 overflow-hidden">
                            <div className="h-full rounded-md" style={{ width: ls.w, background: ls.bg }} />
                          </div>
                        </td>
                        <td className="py-2 px-2.5"><Cnt n={s.arrivals.count} tone="g" /><DogChips dogs={s.arrivals.dogs} max={6} tone="g" doneKey={isToday ? "arrived" : null} /></td>
                        <td className="py-2 px-2.5"><Cnt n={s.departures.count} tone="b" /><DogChips dogs={s.departures.dogs} max={6} tone="b" doneKey={isToday ? "departed" : null} /></td>
                        <td className="py-2 px-2.5"><Cnt n={s.baths.count} tone="a" /><DogChips dogs={s.baths.dogs} max={4} tone="a" /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="flex gap-4 flex-wrap text-xs text-gray-500 mt-3">
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-green-500" /> Arrivals</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-blue-500" /> Departures</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-500" /> Baths</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-rose-500" /> Peak load — staff up</span>
                {isToday && <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-indigo-500" /> NOW</span>}
                {isToday && <span className="line-through text-gray-400">struck = already in/out</span>}
                <span className="text-gray-400">↻ = standing reservation</span>
              </div>
            </div>
          ) : (
            // Histogram
            <div className="keep-color" data-testid="todays-view-histogram">
              <div className="flex items-end gap-1 h-[200px] border-b-2 border-gray-200 pb-0.5">
                {slots.map((s) => (
                  <div key={s.minutes} className="flex-1 flex items-end justify-center gap-0.5 h-full" title={`${s.time} · ${s.arrivals.count} in / ${s.departures.count} out`}>
                    <div className="w-1/2 bg-green-500 rounded-t" style={{ height: `${(s.arrivals.count / maxCount) * 100}%` }} />
                    <div className="w-1/2 bg-blue-500 rounded-t" style={{ height: `${(s.departures.count / maxCount) * 100}%` }} />
                  </div>
                ))}
              </div>
              <div className="flex gap-1 mt-1.5">
                {slots.map((s, i) => (
                  <span key={s.minutes} className="flex-1 text-center text-[9px] text-gray-400 whitespace-nowrap overflow-visible">{i % (slots.length > 16 ? 4 : 2) === 0 ? s.time : ""}</span>
                ))}
              </div>
              <div className="flex gap-4 text-xs text-gray-500 mt-3 mb-4">
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-green-500" /> Arrivals</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-blue-500" /> Departures</span>
              </div>
              {/* Sorted dog lists */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="border border-gray-100 rounded-xl p-3">
                  <div className="text-[11px] font-bold text-green-700 uppercase tracking-wide mb-2 flex items-center gap-1.5"><LogIn className="h-3.5 w-3.5" /> Arrivals by time</div>
                  <div className="space-y-1 max-h-72 overflow-y-auto">
                    {slots.flatMap(s => s.arrivals.dogs).map((d, i) => (
                      <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-gray-50">
                        <span className={`font-semibold text-gray-700 ${isToday && d.arrived ? "line-through opacity-40" : ""}`}>{d.name}{d.is_standing ? " ↻" : ""}</span>
                        <span className="text-gray-400 tabular-nums">{d.time}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="border border-gray-100 rounded-xl p-3">
                  <div className="text-[11px] font-bold text-blue-700 uppercase tracking-wide mb-2 flex items-center gap-1.5"><LogOut className="h-3.5 w-3.5" /> Departures by time</div>
                  <div className="space-y-1 max-h-72 overflow-y-auto">
                    {slots.flatMap(s => s.departures.dogs).map((d, i) => (
                      <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-gray-50">
                        <span className={`font-semibold text-gray-700 ${isToday && d.departed ? "line-through opacity-40" : ""}`}>{d.name}{d.is_standing ? " ↻" : ""}</span>
                        <span className="text-gray-400 tabular-nums">{d.time}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}


// ─── Booking Curve Tab ────────────────────────────────────────────────
function BookingCurveTab() {
  const [view, setView] = useState('lodging');
  const [selectedDate, setSelectedDate] = useState(() => localToday());

  const handleSelectDate = useCallback((dateStr) => {
    setSelectedDate(dateStr);
    setTimeout(() => {
      document.querySelector('[data-testid="todays-view"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  }, []);
  const resetToday = useCallback(() => handleSelectDate(localToday()), [handleSelectDate]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showYoY, setShowYoY] = useState(false);
  const fmtISO = (d) => { const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000); return z.toISOString().split('T')[0]; };
  const [startDate, setStartDate] = useState(() => fmtISO(new Date(Date.now() - 14 * 86400000)));
  const [endDate, setEndDate] = useState(() => fmtISO(new Date(Date.now() + 14 * 86400000)));
  const [services, setServices] = useState(['boarding', 'daycare']);
  const chartRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ops/booking-curve?start_date=${startDate}&end_date=${endDate}&include_yoy=${showYoY}`);
      setData(res.data);
    } catch (e) {
      toast.error("Failed to load booking curve");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, showYoY]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const todayStr = localToday();

  const resetRange = useCallback(() => {
    setStartDate(fmtISO(new Date(Date.now() - 14 * 86400000)));
    setEndDate(fmtISO(new Date(Date.now() + 14 * 86400000)));
  }, []);

  const toggleService = useCallback((key) => {
    setServices((prev) => {
      if (prev.includes(key)) {
        if (prev.length === 1) return prev;
        return prev.filter((k) => k !== key);
      }
      return SERVICE_DEFS.filter((s) => prev.includes(s.key) || s.key === key).map((s) => s.key);
    });
  }, []);

  const getYoyForDate = (dateStr) => (data?.yoy ? data.yoy.find((y) => y.date_current_year === dateStr) : null);

  const chartData = useMemo(() => {
    if (!data?.daily) return [];
    return data.daily.map((day) => {
      const d = new Date(day.date + 'T12:00:00');
      const row = {
        date: day.date,
        weekday: d.toLocaleDateString('en-US', { weekday: 'short' }),
        isWeekend: d.getDay() === 0 || d.getDay() === 6,
      };
      SERVICE_DEFS.forEach((s) => { row[s.key] = day[s.key] || 0; });
      if (showYoY) {
        const yoy = getYoyForDate(day.date);
        row.yoyTotal = yoy ? services.reduce((a, k) => a + (yoy[k] || 0), 0) : null;
      }
      return row;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, showYoY, services]);

  // Label every day; only thin ticks for very long ranges to avoid unreadable overlap.
  const narrow = useIsNarrow();
  const tickInterval = narrow ? Math.max(0, Math.ceil(chartData.length / 7) - 1) : chartData.length > 60 ? Math.floor(chartData.length / 30) : 0;

  return (
    <div className="space-y-4">
      {/* View toggle */}
      <div className="flex items-center gap-2 border-b border-gray-200 pb-3">
        <Button variant={view === 'lodging' ? 'default' : 'outline'} size="sm" onClick={() => setView('lodging')} className="gap-2" data-testid="view-lodging">
          <CalendarDays className="h-4 w-4" /> Lodging
        </Button>
        <Button variant={view === 'forecast' ? 'default' : 'outline'} size="sm" onClick={() => setView('forecast')} className="gap-2" data-testid="view-forecast">
          <TrendingUp className="h-4 w-4" /> Forecast
        </Button>
      </div>

      {view === 'forecast' ? (
        <ForecastCalendar />
      ) : (
      <>
      <TodaysView selectedDate={selectedDate} onResetToday={resetToday} />
      <LodgingCalendar selectedDate={selectedDate} onSelectDate={handleSelectDate} />
      <div className="bg-white border border-gray-200 rounded-2xl p-3 sm:p-5 shadow-sm space-y-4 overflow-hidden" data-testid="booking-chart-card">
        <OpsSectionHead icon={TrendingUp} title="Booking Curve" sub="How far ahead stays are booked vs last year" />

        <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
          <DateRangePickers startDate={startDate} endDate={endDate} onStart={setStartDate} onEnd={setEndDate} onReset={resetRange} />
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Services (stacked)</span>
            <div className="flex flex-wrap items-center gap-1.5">
              {SERVICE_DEFS.map((s) => {
                const on = services.includes(s.key);
                return (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => toggleService(s.key)}
                    aria-pressed={on}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-all ${on ? 'text-white shadow-sm' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
                    style={on ? { backgroundColor: s.color, borderColor: s.color } : undefined}
                    data-testid={`svc-${s.key}`}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: on ? '#ffffff' : s.color }} />
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex items-center gap-1.5 ml-auto">
            <Button variant={showYoY ? 'default' : 'outline'} size="sm" className="text-xs h-8 px-3" onClick={() => setShowYoY(!showYoY)} data-testid="toggle-yoy">YoY</Button>
            <Button variant="outline" size="sm" className="h-8 px-2" onClick={fetchData} disabled={loading} data-testid="chart-refresh">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {loading && !data ? (
          <div className="flex items-center justify-center py-16"><RefreshCw className="h-8 w-8 animate-spin text-gray-400" /></div>
        ) : !chartData.length ? (
          <Card className="p-8 text-center">
            <TrendingUp className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No booking data for this range</p>
          </Card>
        ) : (
          <div data-testid="booking-chart" ref={chartRef} className="w-full min-w-0 overflow-hidden">
            <ResponsiveContainer width="100%" height={narrow ? 280 : 380}>
              <ComposedChart data={chartData} margin={{ top: 8, right: narrow ? 4 : 18, left: narrow ? -8 : 0, bottom: narrow ? 8 : 24 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis
                  dataKey="date"
                  interval={tickInterval}
                  angle={narrow ? 0 : -45}
                  textAnchor={narrow ? "middle" : "end"}
                  height={narrow ? 28 : 58}
                  tickMargin={narrow ? 6 : 8}
                  tick={{ fontSize: narrow ? 9 : 10, fill: '#6b7280' }}
                  tickLine={false}
                  axisLine={{ stroke: '#e5e7eb' }}
                  tickFormatter={(v) => new Date(v + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis
                  allowDecimals={false}
                  width={narrow ? 34 : 46}
                  tick={{ fontSize: narrow ? 9 : 11, fill: '#6b7280' }}
                  tickLine={false}
                  axisLine={{ stroke: '#e5e7eb' }}
                  label={narrow ? undefined : { value: 'Dogs', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#9ca3af', textAnchor: 'middle' } }}
                />
                <RechartsTooltip content={<BookingTooltip services={services} showYoY={showYoY} />} />
                <Legend verticalAlign="top" align="center" height={28} wrapperStyle={{ fontSize: narrow ? 11 : 12, paddingBottom: 6 }} />
                <ReferenceLine x={todayStr} stroke="#13212A" strokeDasharray="4 3" label={{ value: 'Today', position: 'insideTopRight', fill: '#13212A', fontSize: 10, fontWeight: 700 }} />
                {SERVICE_DEFS.filter((s) => services.includes(s.key)).map((s) => (
                  <Bar key={s.key} dataKey={s.key} stackId="svc" name={s.label} fill={s.color} radius={[2, 2, 0, 0]} maxBarSize={46} />
                ))}
                {showYoY && (
                  <Line type="monotone" dataKey="yoyTotal" name="Last Year (total)" stroke="#5B7480" strokeWidth={2} strokeDasharray="4 3" dot={false} connectNulls />
                )}
              </ComposedChart>
            </ResponsiveContainer>
            <p className="text-[11px] text-gray-400 mt-1 flex items-center gap-1"><Info className="h-3 w-3" /> Weekends and today are marked &middot; hover a bar for the daily breakdown &middot; cached ~4 hrs</p>
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
}

// Per-row nudge queue controls (Queue / Snooze / Exclude / status)
function NudgeActions({ dog, q, onAdd, onSnooze, onExclude, onRemove }) {
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [excludeOpen, setExcludeOpen] = useState(false);
  const [until, setUntil] = useState(() => { const d = new Date(); d.setDate(d.getDate() + 14); return d.toISOString().split('T')[0]; });
  const [reason, setReason] = useState('');
  if (q) {
    const label = q.status === 'queued' ? (q.attempts > 0 ? `Queued · ${q.attempts}× sent` : 'Queued')
      : q.status === 'snoozed' ? `Snoozed → ${(q.snooze_until || '').slice(0, 10)}`
      : q.status === 'excluded' ? 'Excluded' : 'Done (max)';
    const color = q.status === 'queued' ? 'bg-blue-100 text-blue-700 border-blue-200'
      : q.status === 'snoozed' ? 'bg-amber-100 text-amber-700 border-amber-200'
      : 'bg-gray-200 text-gray-600 border-gray-300';
    const textedOn = q.last_nudged_at ? new Date(q.last_nudged_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null;
    return (
      <div className="flex flex-col items-center gap-1">
        <div className="flex items-center gap-1.5 justify-center">
          <Badge className={`${color} text-xs`} title={q.exclude_reason || ''} data-testid={`nudge-status-${dog.animal_id}`}>{label}</Badge>
          <button onClick={() => onRemove(dog.animal_id)} title="Remove from queue" className="text-gray-400 hover:text-red-500 text-lg leading-none" data-testid={`nudge-remove-${dog.animal_id}`}>×</button>
        </div>
        {textedOn && (
          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600" title={`Last texted ${new Date(q.last_nudged_at).toLocaleString()}`} data-testid={`nudge-texted-${dog.animal_id}`}>
            <Check className="h-3 w-3" /> Texted {textedOn}
          </span>
        )}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1 justify-center flex-nowrap">
      <Button size="sm" variant="outline" className="text-xs h-7 gap-1" onClick={() => onAdd(dog)} data-testid={`nudge-queue-${dog.animal_id}`}><MessageSquare className="h-3 w-3" />Queue</Button>
      <Popover open={snoozeOpen} onOpenChange={setSnoozeOpen}>
        <PopoverTrigger asChild><Button size="sm" variant="ghost" className="text-xs h-7" data-testid={`nudge-snooze-${dog.animal_id}`}>Snooze</Button></PopoverTrigger>
        <PopoverContent className="ops-v3 w-56 p-3" align="end">
          <div className="text-xs font-semibold text-gray-500 mb-2">Snooze until (e.g. they're away)</div>
          <Input type="date" value={until} onChange={e => setUntil(e.target.value)} className="h-9 text-sm mb-2" data-testid={`nudge-snooze-date-${dog.animal_id}`} />
          <Button size="sm" className="w-full" onClick={() => { onSnooze(dog, new Date(until + 'T00:00:00').toISOString(), 'Snoozed by staff'); setSnoozeOpen(false); }} data-testid={`nudge-snooze-save-${dog.animal_id}`}>Snooze</Button>
        </PopoverContent>
      </Popover>
      <Popover open={excludeOpen} onOpenChange={setExcludeOpen}>
        <PopoverTrigger asChild><Button size="sm" variant="ghost" className="text-xs h-7 text-red-500" data-testid={`nudge-exclude-${dog.animal_id}`}>Exclude</Button></PopoverTrigger>
        <PopoverContent className="w-64 p-3" align="end">
          <div className="text-xs font-semibold text-gray-500 mb-2">Why leave this dog alone?</div>
          <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. needs to be neutered first" className="h-9 text-sm mb-2" data-testid={`nudge-exclude-reason-${dog.animal_id}`} />
          <Button size="sm" variant="destructive" className="w-full" onClick={() => { onExclude(dog, reason || 'Excluded by staff'); setExcludeOpen(false); }} data-testid={`nudge-exclude-save-${dog.animal_id}`}>Exclude</Button>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function NudgeSettings({ cfg, onSave }) {
  const [open, setOpen] = useState(false);
  const [c, setC] = useState(cfg || {});
  useEffect(() => { if (open) setC(cfg || {}); }, [open, cfg]);
  if (!cfg) return null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild><Button variant="outline" size="sm" className="text-xs gap-1" data-testid="nudge-settings-btn"><Info className="h-3.5 w-3.5" />Nudge settings</Button></PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="end">
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between gap-2"><span>Mode</span>
            <select value={c.mode} onChange={e => setC({ ...c, mode: e.target.value })} className="border rounded px-2 py-1 text-xs" data-testid="nudge-mode-select">
              <option value="rehearse">Rehearse (dry run)</option>
              <option value="live">Live (send texts)</option>
            </select>
          </div>
          <div className="flex items-center justify-between gap-2"><span>Cooldown (days)</span><Input type="number" value={c.cooldown_days} onChange={e => setC({ ...c, cooldown_days: parseInt(e.target.value) || 0 })} className="h-8 w-20 text-xs" data-testid="nudge-cooldown" /></div>
          <div className="flex items-center justify-between gap-2"><span>Max attempts</span><Input type="number" value={c.max_attempts} onChange={e => setC({ ...c, max_attempts: parseInt(e.target.value) || 0 })} className="h-8 w-20 text-xs" data-testid="nudge-maxattempts" /></div>
          {c.mode === 'live' && <p className="text-[11px] text-amber-600 font-medium">Live mode will TEXT customers on the next scheduled run.</p>}
          <Button size="sm" className="w-full" onClick={() => { onSave(c); setOpen(false); }} data-testid="nudge-settings-save">Save</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ─── Churn Analysis Tab ───────────────────────────────────────────────
function ChurnTab() {
  const [data, setData] = useState([]);
  const [gingrUrl, setGingrUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [sortField, setSortField] = useState('visits_this_year');
  const [sortDir, setSortDir] = useState('desc');
  const [copiedId, setCopiedId] = useState(null);
  const [queueMap, setQueueMap] = useState({});
  const [nudgeCfg, setNudgeCfg] = useState(null);
  const [rehearsing, setRehearsing] = useState(false);
  const [queueingAll, setQueueingAll] = useState(false);

  const dogPayload = (c) => ({ animal_id: String(c.animal_id), name: c.name, breed: c.breed, owner_id: String(c.owner_id || ''), owner_name: c.owner_name, owner_first: c.owner_first || '' });
  const fetchQueue = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ops/churn-nudge/queue`);
      const m = {}; (res.data.items || []).forEach(i => { m[String(i.animal_id)] = i; });
      setQueueMap(m); setNudgeCfg(res.data.config || null);
    } catch { /* non-fatal */ }
  }, []);
  const qAction = async (p) => { try { await p; } catch { toast.error("Couldn't save — try again"); } fetchQueue(); };
  const addToQueue = (c) => { toast.success(`${c.name} added to nudge queue`); qAction(axios.post(`${API}/ops/churn-nudge/queue`, dogPayload(c))); };
  const snoozeDog = (c, until, note) => { toast.success(`${c.name} snoozed`); qAction(axios.post(`${API}/ops/churn-nudge/snooze`, { animal_id: String(c.animal_id), snooze_until: until, note, dog: dogPayload(c) })); };
  const excludeDog = (c, reason) => { toast.success(`${c.name} excluded`); qAction(axios.post(`${API}/ops/churn-nudge/exclude`, { animal_id: String(c.animal_id), reason, dog: dogPayload(c) })); };
  const removeFromQueue = (aid) => qAction(axios.delete(`${API}/ops/churn-nudge/queue/${aid}`));
  const saveNudgeCfg = async (c) => { try { await axios.put(`${API}/ops/churn-nudge/config`, c); toast.success('Nudge settings saved'); fetchQueue(); } catch { toast.error('Save failed'); } };
  const runRehearse = async () => {
    setRehearsing(true);
    try {
      const r = await axios.post(`${API}/ops/churn-nudge/rehearse`);
      toast.success(`Rehearsal (dry run — no texts sent): would text ${r.data.would_nudge.length}, skipped ${r.data.skipped.length}`, { duration: 7000 });
    } catch { toast.error('Rehearse failed'); } finally { setRehearsing(false); }
  };

  const queueAll = async () => {
    setQueueingAll(true);
    try {
      const { data } = await axios.post(`${API}/ops/churn-nudge/queue-all`);
      if (data.added > 0) toast.success(`Queued ${data.added} at-risk dog${data.added === 1 ? '' : 's'}${data.already_queued ? ` · ${data.already_queued} already handled` : ''}`, { duration: 6000 });
      else toast.info(`All ${data.total_at_risk} at-risk dogs are already queued or handled`, { duration: 6000 });
      fetchQueue();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not queue all');
    } finally { setQueueingAll(false); }
  };

  // Get location name from header (DOM) for the nudge message
  const locationName = document.querySelector('[data-testid="location-selector"]')?.textContent?.trim() || 'K9 Resorts';

  const fetchChurn = useCallback(async (force = false) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ops/churn-analysis`, { params: force ? { force: true } : {}, timeout: 120000 });
      setData(res.data?.churned || []);
      setGingrUrl(res.data?.gingr_url || null);
    } catch {
      toast.error("Failed to load churn analysis");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchChurn(); }, [fetchChurn]);
  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const handleNudge = async (churn) => {
    const ownerFirst = churn.owner_first || churn.owner_name.split(' ')[0];
    const msg = `Hi ${ownerFirst}, we miss having ${churn.name} over at daycare at ${locationName}! We'd love to see ${churn.name} again soon. Let us know if you'd like to book a visit!`;

    try {
      await navigator.clipboard.writeText(msg);
      setCopiedId(churn.animal_id);
      toast.success(`Message copied! Now paste in Gingr's Conversations tab.`, { duration: 4000 });
      setTimeout(() => setCopiedId(null), 3000);

      // Open owner's Gingr page in new tab
      if (gingrUrl && churn.owner_id) {
        window.open(`https://${gingrUrl}/owners/view/id/${churn.owner_id}`, '_blank');
      }
    } catch {
      // Fallback: select text from a temp textarea
      const ta = document.createElement('textarea');
      ta.value = msg;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopiedId(churn.animal_id);
      toast.success(`Message copied!`);
      setTimeout(() => setCopiedId(null), 3000);
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 text-gray-300" />;
    return sortDir === 'asc' ? <ArrowUp className="h-3 w-3 text-amber-600" /> : <ArrowDown className="h-3 w-3 text-amber-600" />;
  };

  const sorted = [...data]
    .filter(c => {
      if (!filter) return true;
      const q = filter.toLowerCase();
      return c.name.toLowerCase().includes(q) || c.owner_name.toLowerCase().includes(q) || c.breed.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      let valA = a[sortField];
      let valB = b[sortField];
      if (sortField === 'last_visit_iso') {
        valA = new Date(valA).getTime();
        valB = new Date(valB).getTime();
      }
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const isParked = (c) => { const q = queueMap[String(c.animal_id)]; return q && ['snoozed', 'excluded', 'done'].includes(q.status); };
  const visible = sorted.filter(c => !isParked(c));
  const parked = sorted.filter(isParked);

  const qList = Object.values(queueMap);
  const qStats = {
    queued: qList.filter(i => i.status === 'queued').length,
    texted: qList.filter(i => i.last_nudged_at).length,
    snoozed: qList.filter(i => i.status === 'snoozed').length,
    excluded: qList.filter(i => i.status === 'excluded').length,
    done: qList.filter(i => i.status === 'done').length,
  };
  const queuedDogs = qList.filter(i => i.status === 'queued');

  return (
    <div className="space-y-4" data-testid="churn-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm text-gray-500">Daycare regulars (avg &le;14 days) who stopped coming &mdash; 6-month analysis, active dogs only</div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-3.5 w-3.5" />
            <Input
              placeholder="Filter by dog, owner, or breed..."
              className="pl-9 h-9 w-64 text-sm"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="churn-filter"
            />
          </div>
          <Badge variant="secondary" className="text-sm font-medium px-3 py-1" data-testid="churn-count">
            {data.length} at risk
          </Badge>
          <Button variant="outline" size="sm" className="text-xs gap-1" onClick={runRehearse} disabled={rehearsing} data-testid="nudge-rehearse-btn">
            <MessageSquare className="h-3.5 w-3.5" /> {rehearsing ? 'Rehearsing…' : 'Rehearse nudges'}
          </Button>
          <Button variant="outline" size="sm" className="text-xs gap-1 border-blue-300 text-blue-700 hover:bg-blue-50" onClick={queueAll} disabled={queueingAll} data-testid="queue-all-btn">
            {queueingAll ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlusCircle className="h-3.5 w-3.5" />} {queueingAll ? 'Queuing…' : 'Queue all at-risk'}
          </Button>
          <NudgeSettings cfg={nudgeCfg} onSave={saveNudgeCfg} />
          <Link to="/agents/churn-nudge" data-testid="to-churn-agent">
            <Button variant="outline" size="sm" className="text-xs gap-1 border-rose-300 text-rose-700 hover:bg-rose-50">
              <MessageSquare className="h-3.5 w-3.5" /> Churn Agent <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </Link>
          <Button variant="outline" size="icon" onClick={() => fetchChurn(true)} disabled={loading} title="Refresh (recompute now)" aria-label="Refresh" className="h-9 w-9">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {qList.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-gradient-to-r from-blue-50/60 via-white to-emerald-50/60 px-4 py-3" data-testid="nudge-queue-glance">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 mr-1">Nudge Queue</span>
              <Badge className="bg-blue-100 text-blue-700 border-blue-200" data-testid="glance-queued">{qStats.queued} queued</Badge>
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200" data-testid="glance-texted"><Check className="h-3 w-3 mr-1" />{qStats.texted} texted</Badge>
              {qStats.snoozed > 0 && <Badge className="bg-amber-100 text-amber-700 border-amber-200">{qStats.snoozed} snoozed</Badge>}
              {qStats.excluded > 0 && <Badge className="bg-gray-200 text-gray-600 border-gray-300">{qStats.excluded} excluded</Badge>}
              {qStats.done > 0 && <Badge className="bg-gray-200 text-gray-600 border-gray-300">{qStats.done} done</Badge>}
            </div>
          </div>
          {queuedDogs.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap mt-2.5" data-testid="glance-queued-chips">
              {queuedDogs.map(d => (
                <span key={d.animal_id} className="inline-flex items-center gap-1 rounded-full bg-white border border-blue-200 px-2.5 py-1 text-xs text-gray-700 shadow-sm" title={d.owner_name || ''}>
                  <Dog className="h-3 w-3 text-blue-500" />
                  <b className="font-semibold">{d.name || 'Dog'}</b>
                  <span className="text-gray-400">·</span>
                  <span className="text-gray-500">{(d.owner_name || '').split(' ')[0]}</span>
                  {d.last_nudged_at && <Check className="h-3 w-3 text-emerald-500" title="Already texted" />}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16"><RefreshCw className="h-8 w-8 animate-spin text-gray-400" /></div>
      ) : sorted.length === 0 ? (
        <Card className="p-8 text-center">
          <CheckCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No churned regulars found</p>
          <p className="text-sm text-gray-400 mt-1">All your daycare regulars are still coming in</p>
        </Card>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="churn-table">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
                <th className="pb-3 pt-3 pl-4 pr-4 cursor-pointer select-none hover:text-gray-700" onClick={() => toggleSort('name')}>
                  <div className="flex items-center gap-1">Dog / Owner <SortIcon field="name" /></div>
                </th>
                <th className="pb-3 pt-3 pr-4 cursor-pointer select-none hover:text-gray-700" onClick={() => toggleSort('avg_freq')}>
                  <div className="flex items-center gap-1">Visit Pattern <SortIcon field="avg_freq" /></div>
                </th>
                <th className="pb-3 pt-3 pr-4 cursor-pointer select-none hover:text-gray-700" onClick={() => toggleSort('last_visit_iso')}>
                  <div className="flex items-center gap-1">Last Seen <SortIcon field="last_visit_iso" /></div>
                </th>
                <th className="pb-3 pt-3 pr-4 cursor-pointer select-none hover:text-gray-700" onClick={() => toggleSort('visits_this_year')}>
                  <div className="flex items-center gap-1">
                    Total Res.
                    <SortIcon field="visits_this_year" />
                    <Popover>
                      <PopoverTrigger asChild>
                        <button className="inline-flex items-center justify-center h-4 w-4 rounded-full border border-gray-300 text-[8px] font-bold text-gray-400 hover:bg-gray-100">i</button>
                      </PopoverTrigger>
                      <PopoverContent className="w-56 p-3 text-xs">
                        <p className="font-bold mb-1">What is Total Res?</p>
                        <p className="text-gray-500">Shows 2026 daycare visits. If zero, shows 2025 count.</p>
                      </PopoverContent>
                    </Popover>
                  </div>
                </th>
                <th className="pb-3 pt-3 pr-4">Status</th>
                <th className="pb-3 pt-3 pr-4 text-center min-w-[380px]">Nudge</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((c, idx) => (
                <tr key={c.animal_id} className={`border-b border-gray-100 hover:bg-blue-50/40 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'}`} data-testid={`churn-row-${c.animal_id}`}>
                  <td className="py-3 pl-4 pr-4">
                    <div className="flex items-center gap-2">
                      <div className="font-semibold text-gray-900">{c.name}</div>
                      {gingrUrl && c.owner_id && (
                        <a
                          href={`https://${gingrUrl}/owners/view/id/${c.owner_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-400 hover:text-blue-500 transition-colors"
                          title="Open in Gingr"
                          data-testid={`gingr-link-${c.animal_id}`}
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </div>
                    <div className="text-xs text-gray-400">{c.owner_name}</div>
                    {c.breed && <div className="text-xs text-gray-400">{c.breed}</div>}
                  </td>
                  <td className="py-3 pr-4 text-gray-700">Every {c.avg_freq} days</td>
                  <td className="py-3 pr-4">
                    <div className="text-gray-700">{c.last_visit}</div>
                    <div className="text-xs text-red-500 font-medium">{c.days_since_last} days ago</div>
                  </td>
                  <td className="py-3 pr-4 text-gray-700">
                    {c.visits_this_year > 0 ? `${c.visits_this_year} (2026)` : `${c.visits_last_year} (2025)`}
                  </td>
                  <td className="py-3 pr-4">
                    <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">At Risk</Badge>
                  </td>
                  <td className="py-3 pr-4 text-center min-w-[380px]">
                    <div className="flex items-center justify-center gap-1.5 flex-nowrap">
                    <NudgeActions dog={c} q={queueMap[String(c.animal_id)]} onAdd={addToQueue} onSnooze={snoozeDog} onExclude={excludeDog} onRemove={removeFromQueue} />
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant={copiedId === c.animal_id ? "default" : "outline"}
                            size="sm"
                            className={`gap-1.5 text-xs ${copiedId === c.animal_id ? 'bg-green-500 hover:bg-green-600 text-white' : ''}`}
                            onClick={() => handleNudge(c)}
                            data-testid={`nudge-btn-${c.animal_id}`}
                          >
                            {copiedId === c.animal_id ? (
                              <><CheckCircle className="h-3.5 w-3.5" /> Copied</>
                            ) : (
                              <><MessageSquare className="h-3.5 w-3.5" /> Nudge</>
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="left" className="max-w-xs text-xs">
                          Copies a "we miss you" message and opens Gingr
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    </div>
                  </td>
                </tr>
              ))}
              {parked.map((c) => { const q = queueMap[String(c.animal_id)]; return (
                <tr key={'p' + c.animal_id} className="border-b border-gray-100 opacity-60 bg-gray-50/40" data-testid={`churn-parked-${c.animal_id}`}>
                  <td className="py-2 pl-4 pr-4"><div className="font-medium text-gray-700">{c.name}</div><div className="text-xs text-gray-400">{c.owner_name}</div></td>
                  <td className="py-2 pr-4 text-xs text-gray-500" colSpan={3}>{q.status === 'snoozed' ? `Snoozed until ${(q.snooze_until || '').slice(0, 10)}` : q.status === 'excluded' ? `Excluded — ${q.exclude_reason || ''}` : 'Completed (max attempts)'}</td>
                  <td className="py-2 pr-4"><Badge className="bg-gray-200 text-gray-600 border-gray-300 text-xs">{q.status}</Badge></td>
                  <td className="py-2 text-center"><Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => removeFromQueue(c.animal_id)} data-testid={`nudge-unpark-${c.animal_id}`}>Pull back</Button></td>
                </tr>); })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Collar / Tag Prep Tab ────────────────────────────────────────────
const COLLAR_META = {
  white:  { label: "White",  sub: "Daycare Eval",   dot: "#ffffff", ring: "ring-1 ring-gray-300", card: "border-gray-200 bg-white",   num: "text-gray-900", head: "bg-gray-50 text-gray-700" },
  yellow: { label: "Yellow", sub: "Boarding · Eval", dot: "#f5c518", ring: "",                     card: "border-amber-200 bg-amber-50/40",   num: "text-amber-500",  head: "bg-amber-50 text-amber-700" },
  green:  { label: "Green",  sub: "Large Group",     dot: "#22c55e", ring: "",                     card: "border-emerald-200 bg-emerald-50/40", num: "text-emerald-600", head: "bg-emerald-50 text-emerald-700" },
  blue:   { label: "Blue",   sub: "Small Group",     dot: "#3b82f6", ring: "",                     card: "border-blue-200 bg-blue-50/40",   num: "text-blue-600",  head: "bg-blue-50 text-blue-700" },
  red:    { label: "Red",    sub: "Private Play",    dot: "#ef4444", ring: "",                     card: "border-red-200 bg-red-50/40",   num: "text-red-600",   head: "bg-red-50 text-red-700" },
  mixed:  { label: "Mixed",  sub: "2+ tags",         dot: "#a855f7", ring: "",                     card: "border-purple-200 bg-purple-50/40", num: "text-purple-600", head: "bg-purple-50 text-purple-700" },
  black:  { label: "Day Boarding", dot: "#1f2937", ring: "ring-1 ring-gray-400" },
};
const COLLAR_ORDER = ["white", "yellow", "green", "blue", "red", "mixed"];

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const parseYmd = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };

function CollarSquare({ color }) {
  const m = COLLAR_META[color] || {};
  return <span className={`inline-block h-3 w-3 rounded-[3px] ${m.ring || 'ring-1 ring-black/10'}`} style={{ background: m.dot }} title={m.label} />;
}

function CollarPrepTab() {
  const initial = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return ymd(d); })();
  const [date, setDate] = useState(initial);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gingrUrl, setGingrUrl] = useState(null);

  const fetchData = useCallback(async (d, silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${API}/ops/collar-prep`, { params: { date: d } });
      setData(res.data);
      setGingrUrl(res.data?.gingr_url || null);
    } catch (e) {
      if (!silent) toast.error("Failed to load collar prep list");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(date); }, [date, fetchData]);
  useEffect(() => {
    const id = setInterval(() => fetchData(date, true), 90000);
    return () => clearInterval(id);
  }, [date, fetchData]);

  const shiftDay = (delta) => { const d = parseYmd(date); d.setDate(d.getDate() + delta); setDate(ymd(d)); };
  const goTomorrow = () => { const d = new Date(); d.setDate(d.getDate() + 1); setDate(ymd(d)); };

  const todayStr = ymd(new Date());
  const tomorrowStr = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return ymd(d); })();
  const relLabel = date === todayStr ? "TODAY" : date === tomorrowStr ? "TOMORROW" : null;
  const friendly = parseYmd(date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const gingrDayLink = gingrUrl ? `https://${gingrUrl}/reservations/by_time/${Math.floor(parseYmd(date).getTime() / 1000)}` : null;

  const counts = data?.counts || {};
  const columns = data?.columns || {};
  const mixed = data?.mixed || [];
  const totalTags = COLLAR_ORDER.reduce((s, c) => s + (counts[c] || 0), 0);
  const totalDogs = data?.total_dogs || 0;

  const listFor = (c) => c === "mixed" ? mixed : (columns[c] || []);

  return (
    <div className="space-y-4" data-testid="collar-prep-tab">
      <OpsSectionHead icon={Tag} title="Collar / Tag Prep" sub="Make these tags tonight — colors match the physical collar tags" />

      {/* Controls: date picker LEFT (matches other tabs), actions RIGHT */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => shiftDay(-1)} data-testid="collar-date-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Input type="date" value={date} onChange={e => e.target.value && setDate(e.target.value)} className="w-[160px]" data-testid="collar-date-picker" />
          <Button variant="outline" size="icon" onClick={() => shiftDay(1)} data-testid="collar-date-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
          {date !== tomorrowStr && (
            <Button variant="ghost" size="sm" onClick={goTomorrow} className="text-xs" data-testid="collar-date-tomorrow">Tomorrow</Button>
          )}
          {relLabel && <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 text-xs px-2 py-0.5">{relLabel}</Badge>}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500" data-testid="collar-summary">
            <span className="font-semibold text-gray-700">{totalDogs} dogs</span>
            {mixed.length > 0 && <span className="text-gray-400"> · {mixed.length} need 2+</span>}
          </span>
          {gingrDayLink && (
            <Button variant="outline" size="sm" asChild data-testid="collar-gingr-btn">
              <a href={gingrDayLink} target="_blank" rel="noopener noreferrer" title="Open this day in Gingr">
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Open in Gingr
              </a>
            </Button>
          )}
          <Button variant="ghost" size="icon" onClick={() => fetchData(date)} data-testid="collar-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {loading && !data ? (
        <div className="py-16 text-center text-gray-400 text-sm">Loading…</div>
      ) : totalTags === 0 ? (
        <Card className="p-10 text-center text-gray-400 text-sm">No collar tags needed for {friendly}.</Card>
      ) : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-3" data-testid="collar-kpis">
            {COLLAR_ORDER.map((c) => {
              const m = COLLAR_META[c];
              return (
                <div key={c} className={`rounded-xl border p-3 ${m.card}`} data-testid={`collar-kpi-${c}`}>
                  <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-gray-500">
                    <span className={`inline-block h-3 w-3 rounded-[3px] ${m.ring || 'ring-1 ring-black/10'}`} style={{ background: m.dot }} />
                    {m.label}
                  </div>
                  <div className="v3-num text-[28px] font-bold leading-none mt-1.5 text-gray-900" data-testid={`collar-count-${c}`}>{counts[c] || 0}</div>
                  <div className="text-[11px] text-gray-400 mt-0.5">{m.sub}</div>
                </div>
              );
            })}
          </div>

          {/* Columns */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {COLLAR_ORDER.map((c) => {
              const m = COLLAR_META[c];
              const items = listFor(c);
              return (
                <div key={c} className="rounded-xl border border-gray-200 bg-white overflow-hidden" data-testid={`collar-col-${c}`}>
                  <div className={`flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-wide border-b border-gray-100 ${m.head}`}>
                    <span className={`inline-block h-3 w-3 rounded-[3px] ${m.ring || 'ring-1 ring-black/10'}`} style={{ background: m.dot }} />
                    {m.label}
                    <span className="ml-auto text-gray-400 font-semibold">{items.length}</span>
                  </div>
                  <ul className="p-1.5 divide-y divide-gray-50">
                    {items.length === 0 ? (
                      <li className="text-center text-[11px] text-gray-300 py-4">—</li>
                    ) : items.map((dog) => (
                      <li key={dog.animal_id} className="flex items-center justify-between gap-1.5 px-2 py-1.5 rounded-lg hover:bg-gray-50" data-testid={`collar-row-${dog.animal_id}`}>
                        <span className="text-sm font-medium text-gray-800 truncate">{dog.animal_name}</span>
                        {c === "mixed" ? (
                          <span className="flex items-center gap-2 flex-shrink-0">
                            {dog.room ? <span className="text-[11px] text-gray-500 font-medium tabular-nums">{dog.room}</span> : null}
                            <span className="flex gap-1">{(dog.flags || []).map((f, i) => <CollarSquare key={i} color={f} />)}</span>
                          </span>
                        ) : (
                          dog.room
                            ? <span className="text-[11px] text-gray-500 font-medium tabular-nums bg-gray-100 rounded px-1.5 py-0.5 flex-shrink-0">{dog.room}</span>
                            : <span className="text-[11px] text-gray-300 flex-shrink-0">—</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Main DailyOps Page ───────────────────────────────────────────────
const OPS_TAB_ACTIVE = "bg-[#1F8A7C] text-white";
export default function DailyOps() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "booking-curve");

  return (
    <div className="ops-v3 w-full max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="daily-ops-page">
      <div>
        <h1 className="text-3xl sm:text-[32px] font-extrabold text-gray-900 mb-1 tracking-tight" style={{ fontFamily: 'Manrope' }}>Daily Operations</h1>
        <p className="text-sm sm:text-base text-gray-500">Everything your team needs for today's shift</p>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setSearchParams(v === "booking-curve" ? {} : { tab: v }); }} className="w-full">
        {/* Desktop: tabs auto-wrap to a second row if they don't fit — the
            ribbon container (white bg + rounded border) always wraps around
            them so no tab appears to bleed outside the card. `flex-wrap`
            handles moderate viewports, `overflow-x-auto` is the safety net
            for extreme narrow widths. */}
        <TabsList className="hidden sm:flex flex-wrap w-full justify-start bg-white border border-gray-200 rounded-xl p-1.5 gap-1.5 h-auto overflow-x-auto" data-testid="ops-tabs">
          <TabsTrigger value="booking-curve" className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg transition-all data-[state=active]:bg-emerald-500 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-emerald-200 data-[state=inactive]:text-gray-500 data-[state=inactive]:hover:bg-gray-100" data-testid="tab-booking-curve">
            <TrendingUp className="h-4 w-4" /> Day View &amp; Booking Curve
          </TabsTrigger>
          <TabsTrigger value="bath-list" className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg transition-all data-[state=active]:bg-blue-500 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-blue-200 data-[state=inactive]:text-gray-500 data-[state=inactive]:hover:bg-gray-100" data-testid="tab-bath-list">
            <Droplets className="h-4 w-4" /> Bath List
          </TabsTrigger>
          <TabsTrigger value="going-home" className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg transition-all data-[state=active]:bg-orange-500 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-orange-200 data-[state=inactive]:text-gray-500 data-[state=inactive]:hover:bg-gray-100" data-testid="tab-going-home">
            <Home className="h-4 w-4" /> Going Home / Belongings
          </TabsTrigger>
          <TabsTrigger value="private-play" className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg transition-all data-[state=active]:bg-purple-500 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-purple-200 data-[state=inactive]:text-gray-500 data-[state=inactive]:hover:bg-gray-100" data-testid="tab-private-play">
            <Dog className="h-4 w-4" /> Private Play
          </TabsTrigger>
          <TabsTrigger value="collars" className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg transition-all data-[state=active]:bg-indigo-500 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-indigo-200 data-[state=inactive]:text-gray-500 data-[state=inactive]:hover:bg-gray-100" data-testid="tab-collars">
            <Tag className="h-4 w-4" /> Collars
          </TabsTrigger>
          <TabsTrigger value="rooms" className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg transition-all data-[state=active]:bg-amber-500 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-amber-200 data-[state=inactive]:text-gray-500 data-[state=inactive]:hover:bg-gray-100" data-testid="tab-rooms">
            <DoorOpen className="h-4 w-4" /> Rooms
          </TabsTrigger>
          <TabsTrigger value="churn" className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg transition-all data-[state=active]:bg-rose-500 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-rose-200 data-[state=inactive]:text-gray-500 data-[state=inactive]:hover:bg-gray-100" data-testid="tab-churn">
            <TrendingDown className="h-4 w-4" /> Churn
          </TabsTrigger>
          <TabsTrigger value="occupancy" className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg transition-all data-[state=active]:bg-teal-500 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-teal-200 data-[state=inactive]:text-gray-500 data-[state=inactive]:hover:bg-gray-100" data-testid="tab-occupancy">
            <Activity className="h-4 w-4" /> Occupancy Trend
          </TabsTrigger>
          <TabsTrigger value="package-renewals" className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg transition-all data-[state=active]:bg-green-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-green-200 data-[state=inactive]:text-gray-500 data-[state=inactive]:hover:bg-gray-100" data-testid="tab-package-renewals">
            <Package className="h-4 w-4" /> Package Renewals
          </TabsTrigger>
          <TabsTrigger value="requested" className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg transition-all data-[state=active]:bg-sky-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-sky-200 data-[state=inactive]:text-gray-500 data-[state=inactive]:hover:bg-gray-100" data-testid="tab-requested">
            <InboxIcon className="h-4 w-4" /> Requested
          </TabsTrigger>
        </TabsList>

        {/* Mobile: 2-row stacked grid */}
        <div className="sm:hidden bg-white border border-gray-200 rounded-xl p-1.5" data-testid="ops-tabs-mobile">
          <div className="grid grid-cols-5 gap-1 mb-1">
            {[
              { value: "booking-curve", label: "Day View", Icon: TrendingUp, active: OPS_TAB_ACTIVE },
              { value: "bath-list", label: "Bath List", Icon: Droplets, active: OPS_TAB_ACTIVE },
              { value: "going-home", label: "Home / Belongings", Icon: Home, active: OPS_TAB_ACTIVE },
              { value: "private-play", label: "Play", Icon: Dog, active: OPS_TAB_ACTIVE },
              { value: "collars", label: "Collars", Icon: Tag, active: OPS_TAB_ACTIVE },
            ].map(tab => (
              <button key={tab.value} onClick={() => setActiveTab(tab.value)}
                className={`flex flex-col items-center justify-center gap-1 px-1 py-2 text-[11px] leading-tight text-center font-semibold rounded-lg transition-all ${
                  activeTab === tab.value ? tab.active : 'text-gray-500 hover:bg-gray-100'
                }`}
                data-testid={`tab-${tab.value}-mobile`}>
                <tab.Icon className="h-3.5 w-3.5" /> {tab.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-5 gap-1">
            {[
              { value: "rooms", label: "Rooms", Icon: DoorOpen, active: OPS_TAB_ACTIVE },
              { value: "churn", label: "Churn", Icon: TrendingDown, active: OPS_TAB_ACTIVE },
              { value: "occupancy", label: "Occupancy", Icon: Activity, active: OPS_TAB_ACTIVE },
              { value: "package-renewals", label: "Renewals", Icon: Package, active: OPS_TAB_ACTIVE },
              { value: "requested", label: "Requested", Icon: InboxIcon, active: OPS_TAB_ACTIVE },
            ].map(tab => (
              <button key={tab.value} onClick={() => setActiveTab(tab.value)}
                className={`flex flex-col items-center justify-center gap-1 px-1 py-2 text-[11px] leading-tight text-center font-semibold rounded-lg transition-all ${
                  activeTab === tab.value ? tab.active : 'text-gray-500 hover:bg-gray-100'
                }`}
                data-testid={`tab-${tab.value}-mobile`}>
                <tab.Icon className="h-3.5 w-3.5" /> {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 min-w-0">
          <TabsContent value="bath-list"><BathListTab /></TabsContent>
          <TabsContent value="going-home"><GoingHomeTab /></TabsContent>
          <TabsContent value="private-play"><PrivatePlayTab /></TabsContent>
          <TabsContent value="collars"><CollarPrepTab /></TabsContent>
          <TabsContent value="rooms"><RoomsTab /></TabsContent>
          <TabsContent value="booking-curve"><BookingCurveTab /></TabsContent>
          <TabsContent value="churn"><ChurnTab /></TabsContent>
          <TabsContent value="occupancy"><OccupancyTrend /></TabsContent>
          <TabsContent value="package-renewals"><PackageRenewalsTab /></TabsContent>
          <TabsContent value="requested"><RequestedTab /></TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
